import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const göteborgKommunerText = getCityMunicipalityList("göteborg");

export const göteborgPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-goteborg",
    serviceCategory: "bortforsling",
    city: "göteborg",

    meta: {
      title: "Bortforsling Göteborg – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Göteborg. Vi hämtar möbler, vitvaror och skräp från alla stadsdelar – Majorna, Hisingen, Linnéstaden och fler. Fast pris, ofta samma dag.",
      h1: "Bortforsling Göteborg",
    },

    hero: {
      heading: "Bortforsling i Göteborg",
      intro:
        "Boka bortforsling i Göteborg – vi hämtar möbler, vitvaror och skrymmande föremål från alla stadsdelar och kranskommuner. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Över 50 000 slutförda uppdrag i hela Sverige.",
      heroImage: "/hero-bortforsling.webp",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Göteborg",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Göteborg – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Göteborg är en stad med branta backar, trånga landshövdingehus och livlig spårvagnstrafik – det gör bortforsling till en utmaning om man ska försöka klara det själv. Med Ungdomskraft slipper du allt krångel. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress, oavsett om du bor i Majorna, på Hisingen, i Linnéstaden eller i någon av Göteborgs kranskommuner som Mölndal, Partille eller Kungsbacka.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet eller ditt hus, lastar i bilen och kör till närmaste återvinningscentral. I Göteborgsområdet arbetar vi främst med Tagene ÅVC, Bulycke ÅVC, Högsbo ÅVC och Sävenäs ÅVC – men vi anpassar oss alltid efter var du befinner dig. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nBortforsling i Göteborg innebär ofta logistiska utmaningar som trånga trapphus i landshövdingehus i Majorna och Linnéstaden, branta backar i Guldheden och Johanneberg, eller begränsade parkeringsmöjligheter i centrala Göteborg. Våra medarbetare är vana vid dessa förutsättningar och har utrustningen som krävs – bärbälten, skyddsfiltar och lastramper. Vi skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Göteborg",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Om du bor i lägenhet, kontrollera om det finns en innergård eller lastzon vi kan använda – det sparar tid jämfört med att parkera på gatan. I centrala Göteborg, särskilt kring Avenyn och Vasaplatsen, kan parkeringssituationen vara utmanande, så meddela oss gärna vid bokningen om du har tillgång till en garageinfart eller innergård.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen eller ett rum. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant kan du lämna gratis på Göteborgs återvinningscentraler.",
      additionalSections: [
        {
          heading: "Stadsdelar och kommuner vi täcker i Göteborg",
          content:
            "Vi utför bortforsling i hela Göteborgsregionen – från centrum till ytterområdena och kranskommunerna. Oavsett om du bor i en etta på Majorna, en villa i Torslanda, ett radhus i Kortedala eller ett kontor på Hisingen kan vi vara på plats, ofta redan samma dag.\n\nI centrala Göteborg (Linnéstaden, Vasastan, Haga, Masthugget, Johanneberg) har vi stor erfarenhet av de specifika utmaningar som äldre byggnader medför – trånga trapphus i landshövdingehus, smala passager och begränsade möjligheter att parkera nära porten. Göteborg har också många byggnader med tre våningar i trä, den så kallade landshövdingehusmodellen, som kräver extra försiktighet vid bärning av tunga föremål.\n\nPå Hisingen (Lundby, Backa, Torslanda, Biskopsgården, Eriksberg) och i östra Göteborg (Kortedala, Gamlestaden, Angered, Örgryte) når vi er snabbt tack vare god tillgång till Götaleden, E6 och E45. Samma gäller för kranskommuner som Mölndal, Partille, Kungälv, Lerum och Kungsbacka – vi tar oss dit utan problem och utan extra avgifter för området.",
        },
        {
          heading: "Återvinning och miljöansvar i Göteborg",
          content:
            "En viktig del av vår bortforsling i Göteborg är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler i Göteborgsområdet. De stationer vi använder mest är:\n\nTagene ÅVC – belägen på norra Hisingen, perfekt för uppdrag i Hisingens stadsdelar och norra Göteborg. Bulycke ÅVC – en stor och välutrustad station i Torslanda, med kapacitet för de flesta materialtyper. Högsbo ÅVC – vår huvudstation för uppdrag i södra Göteborg och västra stadsdelarna. Sävenäs ÅVC – centralt belägen och bra för uppdrag i östra Göteborg.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Myrorna och Erikshjälpen, som båda har en stark närvaro i Göteborgsområdet. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen. Du kan dessutom ha rätt till RUT-avdrag om föremålen transporteras till återbruk.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Göteborg med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Göteborgs stadsdelar, trafikmönster och återvinningscentraler utan och innan. Vi vet vilka gator som har spårvagnsspår, vilka backar som kräver extra planering och var det finns lastmöjligheter – det håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Göteborgsregionen. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för centrala Göteborg, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan. Det som går att donera skänks vidare till Myrorna och Erikshjälpen. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Göteborg?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Göteborg?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Göteborg?",
        answer:
          "Vi har skåpbilar och personal i hela Göteborgsregionen och kan ofta vara på plats redan samma dag. Vid akuta behov kan vi ibland komma inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Göteborg?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Göteborg?",
        answer:
          "Absolut. Vi är vana vid Göteborgs trånga trapphus, speciellt i landshövdingehus i Majorna, Linnéstaden och Haga. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Göteborg försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Hur hanterar ni återvinning efter bortforsling i Göteborg?",
        answer:
          "Vi sorterar allt material och kör det till godkända återvinningscentraler som Tagene, Bulycke, Högsbo och Sävenäs ÅVC. Det som kan återanvändas skänks till Myrorna eller Erikshjälpen. Du kan ha rätt till RUT-avdrag vid transport till återbruk.",
      },
      {
        question: "Kan jag boka bortforsling i Göteborg på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller förråd i Göteborg?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, förråd, vindsutrymmen och garage. Vi är vana vid de trånga passagerna som är vanliga i Göteborgs fastigheter.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Göteborg?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din lägenhet i Linnéstaden och ditt förråd i Högsbo.",
      },
      {
        question: "Vilka stadsdelar i Göteborg täcker ni?",
        answer:
          "Vi utför bortforsling i hela Göteborg – centrum (Linnéstaden, Haga, Masthugget, Johanneberg, Vasastan), Hisingen (Lundby, Backa, Torslanda, Eriksberg), östra Göteborg (Kortedala, Gamlestaden, Örgryte) och alla övriga stadsdelar.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Göteborg?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant kan du lämna gratis på Göteborgs återvinningscentraler, till exempel Tagene eller Högsbo ÅVC.",
      },
      {
        question: "Behöver jag vara hemma vid bortforslingen?",
        answer:
          "Vi rekommenderar att du är hemma eller att någon annan vuxen finns på plats för att visa vad som ska hämtas. Det går också att lämna instruktioner om föremålen står lättillgängligt, till exempel i ett öppet förråd eller utanför porten.",
      },
      {
        question: "Vilka kommuner runt Göteborg utför ni bortforsling i?",
        answer: `Vi täcker hela Göteborgsregionen, inklusive följande kommuner: ${göteborgKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Göteborg i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Göteborg kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus och branta backar – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Göteborg idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Göteborgsregionen.",
    },

    en: {
      slug: "junk-removal-gothenburg",
      meta: {
        title: "Junk Removal Gothenburg – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Gothenburg. We pick up furniture, appliances and bulky items from all neighborhoods. Fixed price, insured, often same day. Book today!",
        h1: "Junk Removal Gothenburg",
      },
      hero: {
        heading: "Junk Removal in Gothenburg",
        intro:
          "Book junk removal in Gothenburg – we collect furniture, appliances and bulky items from all neighborhoods and surrounding municipalities. Quick booking, fixed price and often available the same day. Over 50,000 completed jobs across Sweden.",
      },
      itemsSection: {
        heading: "Items we remove in Gothenburg",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Gothenburg – fast, hassle-free and responsible",
        mainText:
          "Gothenburg is a city with steep hills, narrow \"landshövdingehus\" (governor's houses) and busy tram traffic – making junk removal a real challenge if you try to do it yourself. With Ungdomskraft, you skip the hassle entirely. We send young, strong workers with a van directly to your address, whether you live in Majorna, on Hisingen, in Linnéstaden or any of Gothenburg's surrounding municipalities like Mölndal, Partille or Kungsbacka.\n\nOur team handles the entire chain: we carry items out of your apartment or house, load them into the van and drive to the nearest recycling centre. In the Gothenburg area, we mainly use Tagene, Bulycke, Högsbo and Sävenäs recycling centres – but we always adapt to where you are located. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nJunk removal in Gothenburg often involves logistical challenges like narrow stairways in the traditional wooden \"landshövdingehus\" in Majorna and Linnéstaden, steep hills in Guldheden and Johanneberg, or limited parking options in central Gothenburg. Our workers are accustomed to these conditions and have the right equipment – carrying belts, protective blankets and loading ramps. We protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Gothenburg",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in an apartment, check if there is a courtyard or loading zone we can use – it saves time compared to parking on the street. In central Gothenburg, especially around Avenyn and Vasaplatsen, parking can be challenging, so let us know when booking if you have access to a garage entrance or courtyard.\n\nGather the items in one place if possible, for example in the hallway or one room. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but you can drop those off for free at Gothenburg's recycling centres.",
        additionalSections: [
          {
            heading: "Neighborhoods and municipalities we cover in Gothenburg",
            content:
              "We perform junk removal across the entire Gothenburg region – from the city centre to the outskirts and surrounding municipalities. Whether you live in an apartment in Majorna, a villa in Torslanda, a townhouse in Kortedala or an office on Hisingen, we can be on site, often the same day.\n\nIn central Gothenburg (Linnéstaden, Vasastan, Haga, Masthugget, Johanneberg) we have extensive experience with the specific challenges that older buildings present – narrow stairways in landshövdingehus, tight passages and limited options for parking near the entrance. Gothenburg also has many three-storey wooden buildings that require extra care when carrying heavy items.\n\nOn Hisingen (Lundby, Backa, Torslanda, Biskopsgården, Eriksberg) and in eastern Gothenburg (Kortedala, Gamlestaden, Angered, Örgryte) we reach you quickly thanks to good access to Götaleden, the E6 and E45 motorways. The same goes for surrounding municipalities like Mölndal, Partille, Kungälv, Lerum and Kungsbacka – we get there without issue and without extra charges for the area.",
          },
          {
            heading: "Recycling and environmental responsibility in Gothenburg",
            content:
              "An important part of our junk removal in Gothenburg is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres in the Gothenburg area. The stations we use most are:\n\nTagene recycling centre – located in northern Hisingen, perfect for jobs on Hisingen and in northern Gothenburg. Bulycke recycling centre – a large and well-equipped station in Torslanda, with capacity for most material types. Högsbo recycling centre – our main station for jobs in southern Gothenburg and the western districts. Sävenäs recycling centre – centrally located and ideal for jobs in eastern Gothenburg.\n\nItems that can be reused are donated to organisations like Myrorna and Erikshjälpen, both of which have a strong presence in the Gothenburg area. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill. You may also be entitled to a RUT tax deduction when items are transported for reuse.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Gothenburg with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Gothenburg's neighborhoods, traffic patterns and recycling centres inside and out. We know which streets have tram tracks, which hills require extra planning and where there are loading options – keeping the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Gothenburg region. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for central Gothenburg, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, walls and belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can. Items suitable for donation are passed on to Myrorna and Erikshjälpen. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Gothenburg cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Gothenburg?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Gothenburg?",
          answer:
            "We have vans and staff across the Gothenburg region and can often be on site the same day. For urgent needs, we can sometimes arrive within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Gothenburg?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal.",
        },
        {
          question: "Can you carry items down stairs in Gothenburg's older buildings?",
          answer:
            "Absolutely. We are used to Gothenburg's narrow stairways, especially in the traditional wooden landshövdingehus in Majorna, Linnéstaden and Haga. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get them through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Gothenburg insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "How do you handle recycling after junk removal in Gothenburg?",
          answer:
            "We sort all materials and drive them to approved recycling centres like Tagene, Bulycke, Högsbo and Sävenäs. Items that can be reused are donated to Myrorna or Erikshjälpen. You may be entitled to a RUT tax deduction for transport to reuse.",
        },
        {
          question: "Can I book junk removal in Gothenburg on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements or storage units in Gothenburg?",
          answer:
            "Yes, we pick up items from all spaces – basements, storage units, attics and garages. We are used to the tight passages common in Gothenburg's buildings.",
        },
        {
          question: "Can you make multiple stops during junk removal in Gothenburg?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your apartment in Linnéstaden and a storage unit in Högsbo, for example.",
        },
        {
          question: "Which neighborhoods in Gothenburg do you cover?",
          answer:
            "We perform junk removal across all of Gothenburg – the city centre (Linnéstaden, Haga, Masthugget, Johanneberg, Vasastan), Hisingen (Lundby, Backa, Torslanda, Eriksberg), eastern Gothenburg (Kortedala, Gamlestaden, Örgryte) and all other neighborhoods.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Gothenburg?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. You can drop those off for free at Gothenburg's recycling centres, such as Tagene or Högsbo.",
        },
        {
          question: "Do I need to be home during the junk removal?",
          answer:
            "We recommend that you or another adult is present to show what needs to be picked up. It is also possible to leave instructions if the items are easily accessible, for example in an open storage unit or outside the entrance.",
        },
        {
          question: "Which municipalities around Gothenburg do you serve?",
          answer:
            "We cover the entire Gothenburg region, including Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Gothenburg in 3 easy steps",
        step2Text:
          "Our young workers in Gothenburg come to you and collect whatever you want removed. We handle heavy lifting, tight staircases and steep hills – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Gothenburg today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the Gothenburg region.",
      },
    },
  },

  {
    slug: "transporthjalp-goteborg",
    serviceCategory: "transport",
    city: "göteborg",

    meta: {
      title: "Transporthjälp Göteborg – Skåpbil & Personal | Ungdomskraft",
      description:
        "Boka transporthjälp i Göteborg. Vi hämtar, bär och levererar möbler, vitvaror och allt annat med skåpbil – Blocket-köp, butikshämtning eller flytt. Fast pris, ofta samma dag.",
      h1: "Transporthjälp Göteborg",
    },

    hero: {
      heading: "Transporthjälp i Göteborg",
      intro:
        "Boka transporthjälp i Göteborg – vi hämtar, bär, lastar och levererar möbler, vitvaror, elektronik och allt annat med skåpbil. Från Blocket-köp och butikshämtning till flytt och förråd. Fast pris, snabb bokning och ofta tillgängliga redan samma dag i hela Göteborgsregionen.",
      heroImage: "/hero-transporthjalp.png",
    },

    featureCards: {
      headingText: "Vi hjälper dig med transporten i Göteborg",
      sinceText: "Transporthjälp sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals kunder med alla typer av transporter i Göteborgsområdet. Möbler, vitvaror, elektronik, kartonger, Blocket-köp, butikshämtningar och flytt – vi tar hand om allt.",
      bookYourText: "Boka din transporthjälp",
      bookDescText:
        "Ange hämtadress och leveransadress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
      vehicleTitleText: "Rätt skåpbil för varje transport",
      vehicleDescText:
        "Vi kommer med rätt storlek på skåpbil – oavsett om du ska köra en enda sak eller fylla hela lastutrymmet. Möbelfiltar, bärremmar och spännband ingår alltid.",
    },

    itemsSection: {
      heading: "Saker vi hjälper dig att transportera i Göteborg",
      items: [
        {
          name: "Möbler",
          description:
            "Soffor, sängar, bord, garderober, hyllor och allt annat – köpt på Blocket, från butik eller vid flytt",
          image: "/sofa.webp",
        },
        {
          name: "Vitvaror",
          description:
            "Tvättmaskin, kylskåp, frys, diskmaskin och spis – vi hanterar tunga vitvaror säkert",
          image: "/washer.webp",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, skärmar, spelkonsoler och ljudanläggningar – varsam hantering",
          image: "/tv.webp",
        },
        {
          name: "Kartonger & lådor",
          description:
            "Flyttkartonger, paket, lådor och säckar – till och från förråd, vid flytt eller leverans",
          image: "/box.webp",
        },
        {
          name: "Trädgård & utemöbler",
          description:
            "Trädgårdsmöbler, grill, krukor, cyklar och säsongsprylar",
          image: "/patio-furniture.webp",
        },
        {
          name: "Övrigt",
          description:
            "Konst, byggmaterial, kontorsutrustning, träningsutrustning – finns det, kör vi det",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Transporthjälp i Göteborg – skåpbil och personal som kör allt från A till B",
      mainText:
        "Ibland behöver du bara hjälp att flytta något från en plats till en annan i Göteborg. En möbel du köpt på Blocket i Majorna, en tvättmaskin som ska till nya lägenheten i Linnéstaden, kartonger till förrådet i Mölndal eller en TV från Elgiganten i Bäckebol. Det är precis det vi gör.\n\nUngdomskrafts transporthjälp i Göteborg ger dig en skåpbil och ett team av starka, motiverade ungdomar som hämtar, bär, lastar, kör och levererar. Vi hämtar på vilken adress som helst i Göteborgsregionen – hemma hos en Blocket-säljare på Hisingen, en butik i Bäckebol, ett utlämningsställe i Partille, ett förråd i Kungsbacka eller din gamla bostad i Örgryte. Vi bär ut, skyddar med möbelfiltar, lastar säkert i skåpbilen och kör till leveransadressen. Där bär vi in och ställer allt på plats.\n\nGöteborg innebär ofta logistiska utmaningar: trånga trapphus i landshövdingehus i Majorna, branta backar i Guldheden och Johanneberg, spårvagnsspår som begränsar parkering i centrala Göteborg och långa avstånd ut till Hisingen och kranskommunerna. Våra medarbetare är vana vid alla dessa förutsättningar och planerar alltid rutten i förväg för att minimera tid och kostnad.",
      preparationHeading: "Förbered inför din transport i Göteborg",
      preparationText:
        "Beskriv så detaljerat du kan vad som ska transporteras – typ av föremål, ungefärlig storlek och vikt. Ju mer vi vet desto bättre kan vi planera fordon och team.\n\nDemontera möbler som inte går genom dörrar hela. Stäng av vatten och el till vitvaror. Koppla loss kablar från elektronik. Ha allt redo vid dörren eller i det rum det ska hämtas. Se till att det finns fri passage genom trapphus och att skåpbilen kan parkera nära porten.\n\nI centrala Göteborg, särskilt kring Avenyn och Vasaplatsen, kan parkeringssituationen vara utmanande – om du har tillgång till innergård, garageinfart eller lastzon, meddela oss det vid bokningen. Ha portkod och nycklar redo för båda adresserna.",
      additionalSections: [
        {
          heading: "Transporthjälp för Blocket-köp i Göteborg",
          content:
            "Hittat en soffa på Blocket i Kortedala, ett matbord på Facebook Marketplace i Mölndal eller ett kylskåp på Tradera i Partille – men har ingen bil? Vi hämtar ditt köp hos säljaren, bär ut, lastar och kör hem till dig. Du slipper hyra bil, koordinera med kompisar eller betala för dyr hemleverans. Ange säljarens adress som hämtpunkt vid bokning – vi sköter resten.\n\nVi hanterar alla typer av Blocket-köp i Göteborgsområdet: soffor, vitvaror, elektronik, möbler, cyklar och allt annat. Vår transporthjälp är det snabbaste och enklaste sättet att hämta ditt köp – ofta redan samma dag som affären.",
        },
        {
          heading: "Transporthjälp från butik i Göteborg",
          content:
            "Köpt möbler på IKEA Bäckebol eller Kållered, en tvättmaskin på Elgiganten i Sisjön eller trädgårdsmöbler på Bauhaus i Mölndal – men leveransen passar inte eller kostar för mycket? Vi hämtar i butiken och kör hem till dig. IKEA, Elgiganten, Mio, JYSK, Power, Bauhaus, Byggmax, Biltema – oavsett butik i Göteborgsområdet hämtar vi och levererar direkt till din adress.\n\nVi är vana vid alla större handelsområden i Göteborg: Bäckebol, Sisjön, Kållered, Frölunda Torg och Nordstan. Du bokar, vi hämtar och levererar – enkelt.",
        },
        {
          heading: "Stadsdelar och kommuner vi kör i",
          content:
            "Vi utför transporthjälp i hela Göteborgsregionen. I centrala Göteborg (Linnéstaden, Haga, Masthugget, Johanneberg, Vasastan) navigerar vi trånga trapphus i landshövdingehus och branta backar med rutin. På Hisingen (Lundby, Backa, Torslanda, Biskopsgården, Eriksberg) och i östra Göteborg (Kortedala, Gamlestaden, Angered, Örgryte) når vi snabbt fram via Götaleden, E6 och E45.\n\nSamma gäller för kranskommuner som Mölndal, Partille, Kungälv, Lerum, Kungsbacka, Härryda och Ale – vi tar oss dit utan extra avgifter. Behöver du transporthjälp mellan två kommuner, till exempel från Mölndal till Kungälv? Inga problem – vi kör tvärs genom regionen.\n\nTransporthjälp ger inte rätt till RUT-avdrag, men flera av våra andra tjänster gör det – exempelvis flytt, bärhjälp och möbelmontering med 50 % avdrag på arbetskostnaden.",
        },
        {
          heading: "Varför boka transporthjälp i Göteborg med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Göteborgs stadsdelar, trafikmönster och handelsområden utan och innan. Vi vet vilka gator som har spårvagnsspår, var man parkerar vid Avenyn och snabbaste vägen från Bäckebol till Majorna.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Göteborgsregionen. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för centrala Göteborg och inga offertförfrågningar.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Dina ägodelar, trapphuset och fastigheten är skyddade under hela transporten.",
            },
            {
              label: "Allt ingår –",
              text: "Skåpbil, personal, bärning i trappor, möbelfiltar, bärremmar och spännband – allt ingår. Du behöver inte lyfta ett finger.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Hur mycket kostar transporthjälp i Göteborg?",
        answer:
          "Priset beror på vad som ska transporteras, avstånd och om bärning i trappor krävs. En enstaka möbel eller vitvara kostar ofta 595–995 kr. Flera tunga saker kan landa på 1 495–2 495 kr. Du ser priset direkt vid bokning – inga dolda avgifter.",
      },
      {
        question: "Kan jag få RUT-avdrag på transporthjälp i Göteborg?",
        answer:
          "Transporthjälp ger i sig inte rätt till RUT-avdrag. RUT gäller hushållsnära tjänster som utförs i din bostad, exempelvis flytt, bärhjälp och möbelmontering. Ungdomskraft erbjuder flera RUT-berättigade tjänster med 50 % avdrag på arbetskostnaden – kombinera enkelt i en bokning.",
      },
      {
        question: "Hur snabbt kan ni hjälpa med transport i Göteborg?",
        answer:
          "Vi har skåpbilar och personal i hela Göteborgsregionen och kan ofta vara på plats redan samma dag. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Ingår skåpbil i transporthjälpen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån vad som ska transporteras – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Kan ni hämta saker jag köpt på Blocket i Göteborg?",
        answer:
          "Ja, vi hämtar ditt Blocket-köp hos säljaren var som helst i Göteborgsregionen, bär ut, lastar och levererar hem till dig. Ange säljarens adress vid bokning.",
      },
      {
        question: "Kan ni hämta från butik i Göteborg?",
        answer:
          "Ja, vi hämtar från IKEA Bäckebol, IKEA Kållered, Elgiganten Sisjön, Mio, JYSK, Power, Bauhaus, Byggmax och alla andra butiker i Göteborgsområdet. Ange butikens adress vid bokning.",
      },
      {
        question: "Bär ni sakerna i trappor i Göteborg?",
        answer:
          "Ja, vi bär i alla typer av trapphus – trånga landshövdingehus i Majorna, svängda trappor i Linnéstaden och trappor utan hiss. Vi har säckkärra, bärremmar och rätt teknik.",
      },
      {
        question: "Skyddar ni sakerna under transport?",
        answer:
          "Ja, vi skyddar med möbelfiltar, bärremmar och spännband. TV-apparater transporteras stående. Vitvaror säkras med remmar. Allt är försäkrat genom Trygg Hansa.",
      },
      {
        question: "Kan ni hjälpa med transport vid flytt i Göteborg?",
        answer:
          "Ja, vår transporthjälp är perfekt när du packat själv men behöver hjälp att köra sakerna till nya bostaden. Vi hämtar, lastar och levererar till din nya adress i Göteborgsregionen.",
      },
      {
        question: "Kan ni köra saker till förråd i Göteborg?",
        answer:
          "Ja, vi kör möbler, kartonger och annat till och från förråd. Vi kör till alla förrådsanläggningar i Göteborgsområdet – Pelican, Shurgard, Green Storage och fler.",
      },
      {
        question: "Kan ni göra flera stopp i samma transport?",
        answer:
          "Ja, du kan lägga till flera hämtadresser och leveransadresser i samma bokning. Praktiskt om du ska hämta från Blocket-säljare i Kortedala och sedan köra till din lägenhet i Linnéstaden.",
      },
      {
        question: "Kan jag boka transporthjälp i Göteborg på helgen?",
        answer:
          "Ja, vi erbjuder transporthjälp alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst.",
      },
      {
        question: "Demonterar ni möbler innan transport?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär. Vi monterar även ihop dem på leveransadressen om du vill.",
      },
      {
        question: "Vilka stadsdelar i Göteborg täcker ni?",
        answer:
          "Vi kör transporthjälp i hela Göteborg – centrum (Linnéstaden, Haga, Masthugget, Johanneberg, Vasastan), Hisingen (Lundby, Backa, Torslanda, Eriksberg), östra Göteborg (Kortedala, Gamlestaden, Örgryte) och alla övriga stadsdelar.",
      },
      {
        question: "Vilka kommuner runt Göteborg erbjuder ni transporthjälp i?",
        answer: `Vi erbjuder transporthjälp i hela Göteborgsregionen: ${göteborgKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Transporthjälp i Göteborg i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Göteborg hämtar sakerna, bär ut, skyddar med möbelfiltar, lastar skåpbilen och kör till leveransadressen. Där bär vi in och ställer allt på plats.",
      step3Title: "Levererat",
      step3Desc:
        "Dina saker står där du vill ha dem – säkert transporterade och snyggt inställda. Klart.",
      ctaHeading: "Boka transporthjälp i Göteborg idag",
      ctaText:
        "Ange hämtadress och leveransadress, beskriv vad som ska transporteras och välj en tid. Du ser priset direkt.",
    },

    en: {
      slug: "transport-help-gothenburg",
      meta: {
        title: "Transport Help Gothenburg – Van & Crew | Ungdomskraft",
        description:
          "Book transport help in Gothenburg. We pick up, carry and deliver furniture, appliances and more with a van. Fixed price, often same day. Book today!",
        h1: "Transport Help Gothenburg",
      },
      hero: {
        heading: "Transport Help in Gothenburg",
        intro:
          "Book transport help in Gothenburg – we pick up, carry, load and deliver furniture, appliances, electronics and anything else with a van. From Blocket purchases and store pickups to moving and storage. Fixed price, quick booking and often available the same day across the Gothenburg region.",
      },
      featureCards: {
        headingText: "We help you with transport in Gothenburg",
        sinceText: "Transport help since 2014",
        bookYourText: "Book your transport help",
        bookDescText:
          "Enter the pickup address and delivery address, describe what needs transporting and pick a time. You see the price instantly.",
        vehicleTitleText: "The right van for every transport",
        vehicleDescText:
          "We arrive with the right size van — whether you're transporting a single item or filling the entire cargo area. Furniture blankets, straps and tie-downs always included.",
      },
      itemsSection: {
        heading: "Things we help you transport in Gothenburg",
        items: [
          {
            name: "Furniture",
            description:
              "Sofas, beds, tables, wardrobes, shelves and everything else — bought on Blocket, from a store or when moving",
            image: "/sofa.webp",
          },
          {
            name: "Appliances",
            description:
              "Washing machines, fridges, freezers, dishwashers and cookers — we handle heavy appliances safely",
            image: "/washer.webp",
          },
          {
            name: "Electronics",
            description:
              "TVs, computers, monitors, game consoles and audio systems — careful handling",
            image: "/tv.webp",
          },
          {
            name: "Boxes & packages",
            description:
              "Moving boxes, parcels, crates and bags — to and from storage, when moving or for delivery",
            image: "/box.webp",
          },
          {
            name: "Garden & outdoor",
            description:
              "Garden furniture, barbecues, pots, bicycles and seasonal items",
            image: "/patio-furniture.webp",
          },
          {
            name: "Everything else",
            description:
              "Artwork, building materials, office equipment, gym equipment — if it exists, we transport it",
            image: "/box.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Transport help in Gothenburg – van and crew that move anything from A to B",
        mainText:
          "Sometimes you just need help moving something from one place to another in Gothenburg. A piece of furniture you bought on Blocket in Majorna, a washing machine going to your new flat in Linnéstaden, boxes heading to storage in Mölndal or a TV from Elgiganten in Bäckebol. That's exactly what we do.\n\nUngdomskraft's transport help in Gothenburg gives you a van and a team of strong, motivated young workers who pick up, carry, load, drive and deliver. We collect from any address across the Gothenburg region — a Blocket seller's home on Hisingen, a store in Bäckebol, a collection point in Partille, a storage facility in Kungsbacka or your old home in Örgryte. We carry out, protect with furniture blankets, load safely in the van and drive to the delivery address. There we carry in and place everything where you want it.\n\nGothenburg often involves logistical challenges: narrow stairways in landshövdingehus in Majorna, steep hills in Guldheden and Johanneberg, tram tracks limiting parking in central Gothenburg and long distances out to Hisingen and the surrounding municipalities. Our workers are accustomed to all these conditions and always plan the route in advance to minimise time and cost.",
        preparationHeading: "Prepare for your transport in Gothenburg",
        preparationText:
          "Describe as thoroughly as you can what needs transporting — type of item, approximate size and weight. The more we know the better we can plan the right vehicle and team.\n\nDisassemble furniture that won't fit through doors whole. Turn off water and electricity to appliances. Disconnect cables from electronics. Have everything ready by the door or in the room where it needs collecting. Make sure there is clear passage through stairwells and the van can park near the entrance.\n\nIn central Gothenburg, especially around Avenyn and Vasaplatsen, parking can be challenging — if you have access to a courtyard, garage entrance or loading zone, let us know when booking. Have entry codes and keys ready for both addresses.",
        additionalSections: [
          {
            heading: "Transport help for Blocket purchases in Gothenburg",
            content:
              "Found a sofa on Blocket in Kortedala, a dining table on Facebook Marketplace in Mölndal or a fridge on Tradera in Partille — but don't have a car? We collect your purchase from the seller, carry out, load and deliver to your home. No need to rent a car, coordinate with friends or pay for expensive delivery. Enter the seller's address as the pickup point when booking — we take care of the rest.\n\nWe handle all types of Blocket purchases across the Gothenburg area: sofas, appliances, electronics, furniture, bicycles and anything else. Our transport help is the fastest and easiest way to collect your purchase — often the same day as the deal.",
          },
          {
            heading: "Transport help from stores in Gothenburg",
            content:
              "Bought furniture at IKEA Bäckebol or Kållered, a washing machine at Elgiganten in Sisjön or garden furniture at Bauhaus in Mölndal — but the delivery doesn't suit you or costs too much? We collect from the store and deliver to your home. IKEA, Elgiganten, Mio, JYSK, Power, Bauhaus, Byggmax, Biltema — whatever the store in the Gothenburg area, we pick up and deliver straight to your door.\n\nWe know all the major retail parks in Gothenburg: Bäckebol, Sisjön, Kållered, Frölunda Torg and Nordstan. You book, we collect and deliver — simple.",
          },
          {
            heading: "Neighborhoods and municipalities we serve",
            content:
              "We provide transport help across the entire Gothenburg region. In central Gothenburg (Linnéstaden, Haga, Masthugget, Johanneberg, Vasastan) we navigate narrow stairways in landshövdingehus and steep hills with routine. On Hisingen (Lundby, Backa, Torslanda, Biskopsgården, Eriksberg) and in eastern Gothenburg (Kortedala, Gamlestaden, Angered, Örgryte) we reach you quickly via Götaleden, the E6 and E45.\n\nThe same goes for surrounding municipalities like Mölndal, Partille, Kungälv, Lerum, Kungsbacka, Härryda and Ale — no extra charges. Need transport between two municipalities, for example from Mölndal to Kungälv? No problem — we drive across the entire region.\n\nTransport help does not qualify for the Swedish RUT tax deduction, but several of our other services do – including moving help, carrying help and furniture assembly with a 50% deduction on the labour cost.",
          },
          {
            heading: "Why book transport help in Gothenburg with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Gothenburg's neighborhoods, traffic patterns and retail parks inside and out. We know which streets have tram tracks, where to park near Avenyn and the fastest route from Bäckebol to Majorna.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Gothenburg region. We can often be on site the same day you book — including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for central Gothenburg and no quote requests.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your belongings, stairwell and property are protected throughout the transport.",
              },
              {
                label: "Everything included –",
                text: "Van, crew, stair carrying, furniture blankets, straps and tie-downs — everything is included. You don't have to lift a finger.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does transport help in Gothenburg cost?",
          answer:
            "The price depends on what needs transporting, distance and whether stair carrying is required. A single piece of furniture or appliance often costs SEK 595–995. Multiple heavy items can cost SEK 1,495–2,495. You see the price instantly when booking — no hidden fees.",
        },
        {
          question: "Can I get a RUT tax deduction on transport help in Gothenburg?",
          answer:
            "Transport help does not qualify for the Swedish RUT tax deduction. RUT applies to household services carried out in your home, such as moving help, carrying help and furniture assembly. Ungdomskraft offers several RUT-eligible services with a 50% deduction on the labour cost – easily combine them in one booking.",
        },
        {
          question: "How quickly can you help with transport in Gothenburg?",
          answer:
            "We have vans and staff across the Gothenburg region and can often be on site the same day. Book online and see available times instantly.",
        },
        {
          question: "Is a van included?",
          answer:
            "Yes, a van is always included. We choose the right size based on what needs transporting — no need to rent a vehicle, drive or refuel.",
        },
        {
          question: "Can you pick up things I bought on Blocket in Gothenburg?",
          answer:
            "Yes, we collect your Blocket purchase from the seller anywhere in the Gothenburg region, carry out, load and deliver to your home. Enter the seller's address when booking.",
        },
        {
          question: "Can you collect from stores in Gothenburg?",
          answer:
            "Yes, we collect from IKEA Bäckebol, IKEA Kållered, Elgiganten Sisjön, Mio, JYSK, Power, Bauhaus, Byggmax and all other stores in the Gothenburg area. Enter the store address when booking.",
        },
        {
          question: "Do you carry items up stairs in Gothenburg?",
          answer:
            "Yes, we carry in all types of stairwells — narrow landshövdingehus in Majorna, winding stairs in Linnéstaden and buildings without lifts. We have hand trucks, straps and the right technique.",
        },
        {
          question: "Do you protect items during transport?",
          answer:
            "Yes, we protect with furniture blankets, carrying straps and tie-downs. TVs are transported upright. Appliances are secured with straps. Everything is insured through Trygg Hansa.",
        },
        {
          question: "Can you help with transport when moving in Gothenburg?",
          answer:
            "Yes, our transport help is perfect when you've packed yourself but need help getting everything to the new home. We collect, load and deliver to your new address anywhere in the Gothenburg region.",
        },
        {
          question: "Can you drive items to storage in Gothenburg?",
          answer:
            "Yes, we drive furniture, boxes and other items to and from storage. We serve all storage facilities in the Gothenburg area — Pelican, Shurgard, Green Storage and more.",
        },
        {
          question: "Can you make multiple stops in the same transport?",
          answer:
            "Yes, you can add multiple pickup and delivery addresses in the same booking. Practical if you need to collect from a Blocket seller in Kortedala and then deliver to your flat in Linnéstaden.",
        },
        {
          question: "Can I book transport help in Gothenburg on weekends?",
          answer:
            "Yes, we offer transport help every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you.",
        },
        {
          question: "Do you disassemble furniture before transport?",
          answer:
            "Yes, we disassemble wardrobes, bed frames, tables and shelves that need taking apart. We also reassemble them at the delivery address if you want.",
        },
        {
          question: "Which neighborhoods in Gothenburg do you cover?",
          answer:
            "We provide transport help across all of Gothenburg — the city centre (Linnéstaden, Haga, Masthugget, Johanneberg, Vasastan), Hisingen (Lundby, Backa, Torslanda, Eriksberg), eastern Gothenburg (Kortedala, Gamlestaden, Örgryte) and all other neighborhoods.",
        },
        {
          question: "Which municipalities around Gothenburg do you serve?",
          answer:
            "We provide transport help across the entire Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Transport help in Gothenburg in 3 easy steps",
        step2Text:
          "Our young workers in Gothenburg collect the items, carry out, protect with furniture blankets, load the van and drive to the delivery address. There we carry in and place everything where you want it.",
        step3Title: "Delivered",
        step3Desc:
          "Your items are where you want them — safely transported and neatly placed. Done.",
        ctaHeading: "Book transport help in Gothenburg today",
        ctaText:
          "Enter the pickup address and delivery address, describe what needs transporting and pick a time. You see the price instantly.",
      },
    },
  },

  {
    slug: "barhjalp-goteborg",
    serviceCategory: "barhjälp",
    city: "göteborg",

    meta: {
      title: "Bärhjälp Göteborg – Starka Armar för Tunga Lyft | Ungdomskraft",
      description:
        "Boka bärhjälp i Göteborg. Vi bär möbler, vitvaror och kassaskåp – uppför trappor i landshövdingehus, genom trånga utrymmen och utan hiss. Fast pris!",
      h1: "Bärhjälp Göteborg",
    },

    hero: {
      heading: "Bärhjälp i Göteborg",
      intro:
        "Boka bärhjälp i Göteborg – våra starka och erfarna ungdomar bär möbler, vitvaror, kassaskåp och annat tungt uppför trappor, genom trånga utrymmen och i hisslösa hus. Landshövdingehus, branta backar och trånga trapphus? Inga problem. Fast pris, snabb bokning och ofta tillgängliga redan samma dag.",
      heroImage: "/hero-barhjalp.png",
    },

    featureCards: {
      sinceText: "Bärhjälp sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals göteborgare med tunga lyft. Våra ungdomar är tränade i lyftteknik och vet hur man skyddar både gods och fastighet i Göteborgs alla typer av trapphus – från landshövdingehus till nybyggda bostadsrätter.",
      bookYourText: "Boka din bärhjälp",
      bookDescText:
        "Ange din adress, beskriv vad som ska bäras och boka en tid som passar dig. Du ser priset direkt.",
      vehicleTitleText: "Rätt utrustning för ditt lyft",
      vehicleDescText:
        "Vi kommer utrustade med bärremmar, möbelfiltar, trappklättrare och skydd för golv och väggar. Rätt verktyg gör jobbet snabbare, säkrare och skonsammare för ditt hem.",
    },

    itemsSection: {
      heading: "Vanliga saker vi hjälper till att bära i Göteborg",
      items: [
        {
          name: "Soffor & fåtöljer",
          description: "Tunga hörnsoffor, bäddsoffor och skrymmande fåtöljer",
          image: "/sofa.webp",
        },
        {
          name: "Vitvaror",
          description: "Tvättmaskiner, torktumlare, kylskåp och frysar",
          image: "/washer.webp",
        },
        {
          name: "Träningsutrustning",
          description: "Crosstrainers, roddmaskiner, löpband och hemmagym",
          image: "/treadmill.webp",
        },
        {
          name: "Byråer & skåp",
          description: "Tunga garderober, byråer och bokhyllor i massivt trä",
          image: "/wardrobe.webp",
        },
        {
          name: "Sängar & madrasser",
          description: "Dubbelsängar, continental-sängar och tunga madrasser",
          image: "/bed.webp",
        },
        {
          name: "Bord & matgrupper",
          description: "Matbord i massivträ, skrivbord och konferensbord",
          image: "/table.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bärhjälp i Göteborg – för det du inte kan bära själv",
      mainText:
        "Göteborg har en unik blandning av byggnadstyper som gör bärhjälp till en av våra mest efterfrågade tjänster. Landshövdingehusen i Majorna, Haga och Linnéstaden – med sina smala trappuppgångar i trä – är en särskild utmaning när tunga möbler ska upp eller ner. De äldre stenhusen i Vasastan och Lorensberg har eleganta men trånga trapphus. Och i villaområdena i Örgryte, Hovås och Askim ska saker ofta bäras nerför branta källartrappor eller upp via invändiga trappor.\n\nUngdomskraft skickar starka, unga och engagerade medarbetare som är utbildade i ergonomisk lyftteknik. De vet hur man vinklar en skrymmande soffa genom ett trångt trapphus i ett landshövdingehus i Majorna, hur man skyddar de ömtåliga trägolven i Haga med filtar och skyddsmattor, och hur man arbetar säkert i branta backar i Guldheden och Johanneberg.\n\nVi planerar varje lyft innan vi börjar: mäter dörröppningar, identifierar risker och bestämmer den bästa vägen. Resultatet är att jobbet går snabbare, ingenting skadas och du slipper den där känslan av att 'hoppas det går bra'. Allt vi bär är försäkrat genom Trygg Hansa, och vi ersätter eventuella skador – något som inte gäller om du ber en kompis om hjälp.",
      preparationHeading: "Förbered inför bärhjälpen i Göteborg",
      preparationText:
        "Se till att vägen är fri: flytta skor, mattor och småmöbler som kan vara i vägen. Mät dörrarna om du har stora möbler – vi behöver veta om vi kan bära igenom eller om demontering krävs. Om möbler ska bäras nerför trappor, se till att trappan är fri och att belysningen fungerar.\n\nI centrala Göteborg, särskilt kring Avenyn, Haga och Linnégatan, kan parkering vara utmanande. Om du har tillgång till innergård, garageinfart eller lastzon, meddela oss det vid bokningen – det sparar tid om saker ska lastas på eller av ett fordon. Ha portkod redo om du bor i ett flerfamiljshus. Ju mer förberett, desto snabbare går det – och du sparar pengar.",
      additionalSections: [
        {
          heading: "Landshövdingehus och trappor i Göteborg – vår specialitet",
          content:
            "Det vanligaste skälet att boka bärhjälp i Göteborg är just trapporna. Göteborgs fastighetsbestånd är unikt i Sverige tack vare landshövdingehusen – trevåningsbyggnader med bottenvåning i sten och övervåningar i trä. De finns framförallt i Majorna, Haga, Linnéstaden och Masthugget. Trapphusen är smala, svängda och helt i trä, med låg takhöjd och trånga avsatser.\n\nÄldre stenhus i Vasastan, Lorensberg och Johanneberg har klassiska, ofta svängda trapphus med högt i tak men begränsad bredd. Nyare bostadsrätter i Eriksberg, Lindholmen och Kvillebäcken på Hisingen har bredare trapphus och hissar, men möbler ska ändå ofta igenom trånga dörröppningar.\n\nMilljonprogramshus i Angered, Bergsjön och Kortedala har breda trapphus men hissar som sällan rymmer stora möbler. Villor i Örgryte, Hovås, Askim och Mölndal har branta invändiga trappor och trånga källartrappor.\n\nVi har bärt möbler i alla dessa typer av trapphus och vet exakt vilken utrustning och teknik som krävs. Med bärremmar, trappklättrare och skyddsmattor löser vi det mesta – och vi skyddar alltid väggar, räcken och golv under hela processen.",
        },
        {
          heading: "Kassaskåp, träningsutrustning och andra extra tunga föremål i Göteborg",
          content:
            "Kassaskåp och tung träningsutrustning kräver särskild hantering. Ett gammalt kassaskåp kan väga 200–400 kg och har en svårhanterad tyngdpunkt. Vi har erfarenhet av att flytta extra tunga föremål i Göteborgs alla typer av trapphus – från trånga landshövdingehustrappor i Majorna till branta villatrappor i Örgryte och Hovås.\n\nVi diskuterar alltid uppdraget i förväg – skicka gärna bilder på föremålet och trapphuset så ger vi en exakt offert. Vi använder bärremmar, trappklättrare och ibland ramp.\n\nBärhjälp är en RUT-berättigad tjänst. Du får avdrag på 50 % av arbetskostnaden – upp till 75 000 kr per person och år. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Bärhjälp vid leverans och installation i Göteborg",
          listItems: [
            {
              label: "Ny möbel levererad till porten",
              text: "Vi bär den till rätt rum, packar upp och monterar vid behov. Perfekt när budföretaget bara lämnar i trapphuset eller vid porten – vanligt i Göteborgs innerstadskvarter.",
            },
            {
              label: "Vitvaror in och ut",
              text: "Vi kopplar loss den gamla tvättmaskinen, bär ut den och bär in den nya. Vanligt i Göteborgs äldre fastigheter där tvättstugan ligger i källaren.",
            },
            {
              label: "Möbler mellan rum",
              text: "Ska soffan byta plats med bokhyllan? Vi ommöblerar snabbt och smidigt utan att golv eller väggar tar stryk.",
            },
            {
              label: "Kontorsflytt inom huset",
              text: "Omorganisering av kontoret i Lindholmen, Mölndal eller centrum – vi bär skrivbord, skåp och hyllor mellan rum eller våningar.",
            },
          ],
          afterContent:
            "Oavsett uppdrag i Göteborg får du ett fast pris i förväg. Inga timkostnader som skenar, inga dolda avgifter.",
        },
        {
          heading: "Varför boka bärhjälp i Göteborg med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi har bärt möbler i alla Göteborgs stadsdelar och vet exakt vilka utmaningar varje typ av trapphus medför – från landshövdingehus i Majorna till villakällare i Örgryte.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har personal i hela Göteborgsregionen och kan ofta vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Om något skadas under bärningen ersätter vi det – trygghet som inte gäller om du ber en kompis.",
            },
            {
              label: "Fast pris –",
              text: "Du ser priset direkt vid bokning. Inga timkostnader som skenar, inga dolda avgifter och inga offertförfrågningar.",
            },
            {
              label: "Rätt utrustning –",
              text: "Vi kommer med bärremmar, trappklättrare, möbelfiltar och hörnskydd. Allt som behövs för att göra jobbet säkert och skadefritt.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bärhjälp i Göteborg?",
        answer:
          "Priset beror på vad som ska bäras, antal trappor och antal bärare. En enkel soffa upp tre trappor landar ofta på 595–1 495 kr. Du ser ett uppskattat pris direkt vid bokning – inga dolda avgifter.",
      },
      {
        question: "Kan jag få RUT-avdrag på bärhjälp i Göteborg?",
        answer:
          "Självklart, bärhjälp ger rätt till RUT-avdrag. Du får avdrag på 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Avdraget gäller arbete i eller i nära anslutning till din bostad. Vi hanterar RUT-avdraget direkt på fakturan, så du betalar bara halva arbetskostnaden.",
      },
      {
        question: "Hur snabbt kan ni komma med bärhjälp i Göteborg?",
        answer:
          "Vi har personal i hela Göteborgsregionen och kan ofta vara på plats redan samma dag. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Kan ni bära upp möbler utan hiss i Göteborg?",
        answer:
          "Ja, det är en av våra vanligaste tjänster i Göteborg. Vi bär regelbundet soffor, vitvaror och annat tungt i hisslösa trapphus – ofta tre till fyra våningar. Vi har bärremmar, trappklättrare och skyddsmattor.",
      },
      {
        question: "Hur många bärare skickar ni?",
        answer:
          "Vi skickar normalt två bärare. Vid extra tunga föremål som kassaskåp eller tung träningsutrustning, eller om det är många saker, kan vi skicka tre eller fyra. Ange vad som ska bäras vid bokning.",
      },
      {
        question: "Kan ni bära i trånga landshövdingehustrappor?",
        answer:
          "Ja, vi har stor erfarenhet av Göteborgs landshövdingehus – särskilt i Majorna, Haga och Linnéstaden. De smala trapphusen i trä kräver särskild teknik och vi vet exakt hur man vinklar skrymmande möbler och skyddar väggar och golv.",
      },
      {
        question: "Ingår skydd av golv och väggar?",
        answer:
          "Ja, vi skyddar alltid golv, dörrkarmar och trapphus med filtar och hörnskydd som en del av tjänsten. Särskilt viktigt i landshövdingehus med ömtåliga trägolv.",
      },
      {
        question: "Kan ni bära ut gamla möbler och bära in nya samma dag?",
        answer:
          "Absolut. Det är vanligt att vi bär ut en gammal soffa eller tvättmaskin och bär in den nya direkt. Beskriv bägge delarna vid bokning – vi löser det på samma besök.",
      },
      {
        question: "Är bärhjälpen i Göteborg försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Om något skadas under bärningen ersätter vi det. Dokumentera gärna föremålets skick med en bild innan vi börjar.",
      },
      {
        question: "Kan ni bära kassaskåp i Göteborg?",
        answer:
          "Ja, vi har erfarenhet av att flytta kassaskåp och andra extra tunga föremål i alla typer av göteborgska trapphus – landshövdingehustrappor, stenhusens svängda trappor och villatrappor. Vi använder bärremmar, trappklättrare och rätt teknik. Skicka gärna bilder så ger vi en exakt offert.",
      },
      {
        question: "Kan jag boka bärhjälp i Göteborg på helgen?",
        answer:
          "Ja, vi erbjuder bärhjälp alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst.",
      },
      {
        question: "Kan ni hjälpa företag med bärhjälp i Göteborg?",
        answer:
          "Ja, vi utför bärhjälp åt både privatpersoner och företag i Göteborg. Vi bär kontorsmöbler, serverrack, kopiatorer och tung kontorsutrustning mellan rum, våningar eller byggnader. Faktura med moms – inga problem.",
      },
      {
        question: "Kan ni bära saker mellan rum inom bostaden?",
        answer:
          "Ja, vi erbjuder bärhjälp även inom bostaden. Flytta en tung soffa till ett annat rum, bära en byrå från övervåningen till källaren eller ommöblera hela vardagsrummet. Vi skyddar golv och dörrar under hela processen.",
      },
      {
        question: "Vilka stadsdelar i Göteborg täcker ni?",
        answer:
          "Vi utför bärhjälp i hela Göteborg – centrum (Linnéstaden, Haga, Masthugget, Johanneberg, Vasastan), Hisingen (Lundby, Backa, Torslanda, Eriksberg), östra Göteborg (Kortedala, Gamlestaden, Örgryte) och alla övriga stadsdelar.",
      },
      {
        question: "Vilka kommuner runt Göteborg erbjuder ni bärhjälp i?",
        answer: `Vi erbjuder bärhjälp i hela Göteborgsregionen: ${göteborgKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.barhjalp,

    pricingRows: getPackagePricingRows("barhjalp", "sv"),

    dynamicHeadings: {
      threeSteps: "Bärhjälp i Göteborg i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Göteborg skyddar golv och väggar, bär föremålet dit det ska – uppför trappor, genom trånga dörrar eller mellan rum – och ser till att allt står säkert på plats.",
      step3Title: "Uppdraget klart",
      step3Desc:
        "Dina saker står säkert på plats. Vi lämnar inte förrän du är nöjd och allt ser bra ut.",
      ctaHeading: "Boka bärhjälp i Göteborg idag",
      ctaText:
        "Ange din adress, beskriv vad som ska bäras och välj en tid som passar. Du ser priset direkt – vi tar hand om det tunga.",
    },

    en: {
      slug: "carrying-help-gothenburg",
      meta: {
        title: "Carrying Help Gothenburg – Strong Arms for Heavy Lifts | Ungdomskraft",
        description:
          "Book heavy lifting help in Gothenburg. We carry furniture, appliances and safes – up stairs in landshövdingehus, tight spaces, no lift needed. Fixed price!",
        h1: "Carrying Help Gothenburg",
      },
      hero: {
        heading: "Carrying Help in Gothenburg",
        intro:
          "Book carrying help in Gothenburg — our strong and experienced young workers carry furniture, appliances, safes and other heavy items up stairs, through tight spaces and in buildings without lifts. Landshövdingehus, steep hills and narrow stairwells? No problem. Fixed price, quick booking and often available the same day.",
      },
      featureCards: {
        sinceText: "Carrying help since 2014",
        bookYourText: "Book your carrying help",
        bookDescText:
          "Enter your address, describe what needs carrying and pick a time that suits you. You see the price instantly.",
        vehicleTitleText: "Right equipment for your lift",
        vehicleDescText:
          "We arrive equipped with carrying straps, furniture blankets, stair climbers and protection for floors and walls. The right tools make the job faster, safer and gentler on your home.",
      },
      itemsSection: {
        heading: "Common items we help carry in Gothenburg",
        items: [
          {
            name: "Sofas & armchairs",
            description: "Heavy corner sofas, sofa beds and bulky armchairs",
            image: "/sofa.webp",
          },
          {
            name: "Appliances",
            description: "Washing machines, tumble dryers, fridges and freezers",
            image: "/washer.webp",
          },
          {
            name: "Gym equipment",
            description: "Cross trainers, rowing machines, treadmills and home gyms",
            image: "/armchair.webp",
          },
          {
            name: "Wardrobes & dressers",
            description: "Heavy wardrobes, dressers and solid-wood bookcases",
            image: "/wardrobe.webp",
          },
          {
            name: "Beds & mattresses",
            description: "Double beds, continental beds and heavy mattresses",
            image: "/bed.webp",
          },
          {
            name: "Tables",
            description: "Solid-wood dining tables, desks and conference tables",
            image: "/table.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Carrying help in Gothenburg — for what you can't carry yourself",
        mainText:
          "Gothenburg has a unique mix of building types that makes carrying help one of our most requested services. The landshövdingehus in Majorna, Haga and Linnéstaden — with their narrow wooden stairways — are a particular challenge when heavy furniture needs going up or down. The older stone buildings in Vasastan and Lorensberg have elegant but narrow stairwells. And in the residential areas of Örgryte, Hovås and Askim, items often need carrying down steep basement stairs or up via internal staircases.\n\nUngdomskraft sends strong, young and committed workers trained in ergonomic lifting technique. They know how to angle a bulky sofa through a narrow stairwell in a landshövdingehus in Majorna, how to protect the delicate wooden floors in Haga with blankets and protective mats, and how to work safely on the steep hills of Guldheden and Johanneberg.\n\nWe plan every lift before we start: measure doorways, identify risks and determine the best route. The result is a faster job, nothing gets damaged and you avoid that nagging feeling of 'hope this goes well'. Everything we carry is insured through Trygg Hansa, and we compensate for any damage — something that doesn't apply when you ask a friend for help.",
        preparationHeading: "Prepare for your carrying help in Gothenburg",
        preparationText:
          "Make sure the route is clear: move shoes, rugs and small furniture that could be in the way. Measure your doorways if you have large furniture — we need to know whether we can carry it through or if disassembly is required. If furniture is being carried down stairs, make sure the stairway is clear and the lighting works.\n\nIn central Gothenburg, especially around Avenyn, Haga and Linnégatan, parking can be challenging. If you have access to a courtyard, garage entrance or loading zone, let us know when booking — it saves time if items need loading onto a vehicle. Have entry codes ready if you live in an apartment building. The more prepared you are, the faster it goes — and you save money.",
        additionalSections: [
          {
            heading: "Landshövdingehus and stairwells in Gothenburg — our speciality",
            content:
              "The most common reason to book carrying help in Gothenburg is the stairs. Gothenburg's building stock is unique in Sweden thanks to the landshövdingehus — three-storey buildings with a stone ground floor and upper floors in wood. They are found primarily in Majorna, Haga, Linnéstaden and Masthugget. The stairwells are narrow, winding and entirely wooden, with low ceilings and tight landings.\n\nOlder stone buildings in Vasastan, Lorensberg and Johanneberg have classic, often winding stairwells with high ceilings but limited width. Newer condos in Eriksberg, Lindholmen and Kvillebäcken on Hisingen have wider stairwells and lifts, but furniture still often needs to pass through tight doorways.\n\nMillion Programme buildings in Angered, Bergsjön and Kortedala have wide stairwells but lifts that rarely accommodate large furniture. Villas in Örgryte, Hovås, Askim and Mölndal have steep internal staircases and tight basement stairs.\n\nWe have carried furniture in all these types of stairwells and know exactly what equipment and technique is required. With carrying straps, stair climbers and protective mats we handle most situations — and we always protect walls, railings and floors throughout the process.",
          },
          {
            heading: "Safes, gym equipment and other extra-heavy items in Gothenburg",
            content:
              "Safes and heavy gym equipment require special handling. An old safe can weigh 200–400 kg and has an awkward centre of gravity. We have experience moving extra-heavy items in all types of Gothenburg stairwells — from narrow landshövdingehus stairs in Majorna to steep villa stairs in Örgryte and Hovås.\n\nWe always discuss the job in advance — send us photos of the item and the stairwell and we'll give you an exact quote. We use carrying straps, stair climbers and sometimes ramps.\n\nCarrying help is eligible for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost – up to SEK 75,000 per person per year. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Carrying help for deliveries and installation in Gothenburg",
            listItems: [
              {
                label: "New furniture delivered to the entrance",
                text: "We carry it to the right room, unpack and assemble if needed. Perfect when the courier just leaves it in the stairwell or at the entrance — common in Gothenburg's inner-city blocks.",
              },
              {
                label: "Appliances in and out",
                text: "We disconnect the old washing machine, carry it out and bring the new one in. Common in Gothenburg's older buildings where the laundry room is in the basement.",
              },
              {
                label: "Furniture between rooms",
                text: "Want to swap the sofa and the bookcase? We rearrange quickly and smoothly without floors or walls taking a beating.",
              },
              {
                label: "In-office moves",
                text: "Office reorganisation in Lindholmen, Mölndal or the city centre — we carry desks, cabinets and shelving between rooms or floors.",
              },
            ],
            afterContent:
              "Whatever the job in Gothenburg, you get a fixed price upfront. No runaway hourly rates, no hidden fees.",
          },
          {
            heading: "Why book carrying help in Gothenburg with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We have carried furniture across all of Gothenburg's neighborhoods and know exactly what challenges each type of stairwell presents — from landshövdingehus in Majorna to villa basements in Örgryte.",
              },
              {
                label: "Fast availability –",
                text: "We have staff across the Gothenburg region and can often be on site the same day you book — including evenings and weekends.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. If anything is damaged during the carrying, we compensate — peace of mind that doesn't apply when you ask a friend.",
              },
              {
                label: "Fixed price –",
                text: "You see the price immediately when booking. No runaway hourly rates, no hidden fees and no quote requests.",
              },
              {
                label: "Right equipment –",
                text: "We arrive with carrying straps, stair climbers, furniture blankets and corner guards. Everything needed to do the job safely and damage-free.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does carrying help in Gothenburg cost?",
          answer:
            "The price depends on what needs carrying, the number of floors and the number of carriers. A single sofa up three flights often costs SEK 595–1,495. You see an estimated price instantly when booking — no hidden fees.",
        },
        {
          question: "Can I get a RUT tax deduction on carrying help in Gothenburg?",
          answer:
            "Of course, carrying help qualifies for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost, up to SEK 75,000 per person per year. The deduction covers work in or near your home. We handle the RUT deduction directly on the invoice, so you only pay half the labour cost.",
        },
        {
          question: "How quickly can you come with carrying help in Gothenburg?",
          answer:
            "We have staff across the Gothenburg region and can often be on site the same day. Book online and see available times instantly.",
        },
        {
          question: "Can you carry furniture up stairs with no lift in Gothenburg?",
          answer:
            "Yes, it's one of our most common services in Gothenburg. We regularly carry sofas, appliances and other heavy items in buildings without lifts — often three to four floors. We have carrying straps, stair climbers and protective mats.",
        },
        {
          question: "How many carriers do you send?",
          answer:
            "We normally send two carriers. For extra-heavy items like safes or heavy gym equipment, or when there are many items, we can send three or four. Specify what needs carrying when booking.",
        },
        {
          question: "Can you carry in narrow landshövdingehus stairwells?",
          answer:
            "Yes, we have extensive experience with Gothenburg's landshövdingehus — especially in Majorna, Haga and Linnéstaden. The narrow wooden stairwells require special technique and we know exactly how to angle bulky furniture and protect walls and floors.",
        },
        {
          question: "Is floor and wall protection included?",
          answer:
            "Yes, we always protect floors, door frames and stairwells with blankets and corner guards as part of the service. Especially important in landshövdingehus with delicate wooden floors.",
        },
        {
          question: "Can you carry out old furniture and bring in new the same day?",
          answer:
            "Absolutely. It's common for us to carry out an old sofa or washing machine and bring in the new one straight away. Describe both parts when booking — we handle it in the same visit.",
        },
        {
          question: "Is carrying help in Gothenburg insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. If anything is damaged during the carrying, we compensate. It helps to document the item's condition with a photo before we start.",
        },
        {
          question: "Can you carry safes in Gothenburg?",
          answer:
            "Yes, we have experience moving safes and other extra-heavy items in all types of Gothenburg stairwells — landshövdingehus stairs, stone building stairwells and villa stairs. We use carrying straps, stair climbers and the right technique. Send us photos and we'll give you an exact quote.",
        },
        {
          question: "Can I book carrying help in Gothenburg on weekends?",
          answer:
            "Yes, we offer carrying help every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you.",
        },
        {
          question: "Do you provide carrying help for businesses in Gothenburg?",
          answer:
            "Yes, we carry items for both private customers and businesses in Gothenburg. We handle office furniture, server racks, copiers and heavy office equipment between rooms, floors or buildings. Invoicing with VAT — no problem.",
        },
        {
          question: "Can you carry items between rooms within the home?",
          answer:
            "Yes, we offer carrying help within the home as well. Move a heavy sofa to another room, carry a dresser from the upper floor to the basement or rearrange an entire living room. We protect floors and doors throughout.",
        },
        {
          question: "Which neighborhoods in Gothenburg do you cover?",
          answer:
            "We provide carrying help across all of Gothenburg — the city centre (Linnéstaden, Haga, Masthugget, Johanneberg, Vasastan), Hisingen (Lundby, Backa, Torslanda, Eriksberg), eastern Gothenburg (Kortedala, Gamlestaden, Örgryte) and all other neighborhoods.",
        },
        {
          question: "Which municipalities around Gothenburg do you serve?",
          answer:
            "We provide carrying help across the entire Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.barhjalp,
      pricingRows: getPackagePricingRows("barhjalp", "en"),
      dynamicHeadings: {
        threeSteps: "Carrying help in Gothenburg in 3 easy steps",
        step2Text:
          "Our workers in Gothenburg protect floors and walls, carry the item where it needs to go — up stairs, through tight doorways or between rooms — and make sure everything is safely in place.",
        step3Title: "Job done",
        step3Desc:
          "Your items are safely in place. We don't leave until you're happy and everything looks right.",
        ctaHeading: "Book carrying help in Gothenburg today",
        ctaText:
          "Enter your address, describe what needs carrying and pick a time. You see the price instantly — we handle the heavy stuff.",
      },
    },
  },

  {
    slug: "flytthjalp-goteborg",
    serviceCategory: "flytt",
    city: "göteborg",

    meta: {
      title: "Flytthjälp Göteborg – Smidig Flytt med Skåpbil | Ungdomskraft",
      description:
        "Boka flytthjälp i Göteborg. Vi packar, bär, kör och lossar – komplett flytt från dörr till dörr i hela Göteborgsregionen. Fast pris, ofta samma dag.",
      h1: "Flytthjälp Göteborg",
    },

    hero: {
      heading: "Flytthjälp i Göteborg",
      intro:
        "Boka flytthjälp i Göteborg – vi fixar hela flytten med skåpbil och ett team av starka, engagerade ungdomar. Från studentflytt i Haga till villaflytt i Örgryte. Vi packar, bär, kör och ställer allt på plats i ditt nya hem. Fast pris, snabb bokning och ofta tillgängliga redan samma dag.",
      heroImage: "/hero-flytthjalp.png",
    },

    featureCards: {
      sinceText: "Flytthjälp sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft hjälpt tusentals göteborgare att flytta smidigt och prisvärt. Våra ungdomar är tränade i packning, lyftteknik och säker lastning – och de känner Göteborgs stadsdelar och trapphus utan och innan.",
      bookYourText: "Boka din flytt",
      bookDescText:
        "Ange gamla och nya adressen, beskriv vad som ska flyttas och välj en tid. Du ser priset direkt – allt ingår.",
      vehicleTitleText: "Skåpbil och personal i ett paket",
      vehicleDescText:
        "Du behöver inte hyra bil eller organisera transport – vi kommer med skåpbil och team. Vi lastar, kör och lossar. Stort, litet, tungt eller skrymmande – vi har rätt fordon och tillräckligt med armar.",
    },

    itemsSection: {
      heading: "Vanliga saker vi flyttar i Göteborg",
      items: [
        {
          name: "Soffor & fåtöljer",
          description: "Hörnsoffor, bäddsoffor och tunga fåtöljer – vi skyddar och bär",
          image: "/sofa.webp",
        },
        {
          name: "Sängar & madrasser",
          description: "Dubbelsängar, continental-sängar och tunga madrasser",
          image: "/bed.webp",
        },
        {
          name: "Bord & stolar",
          description: "Matbord, skrivbord, stolar och matgrupper",
          image: "/table.webp",
        },
        {
          name: "Vitvaror",
          description: "Tvättmaskin, torktumlare, kylskåp och diskmaskin",
          image: "/washer.webp",
        },
        {
          name: "Garderober & byråer",
          description: "PAX-garderober, byråer, bokhyllor och hyllsystem",
          image: "/wardrobe.webp",
        },
        {
          name: "Flyttkartonger",
          description: "Fulla kartonger med böcker, kläder, kök och prylar",
          image: "/box.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Komplett flytthjälp i Göteborg – skåpbil, personal och allt däremellan",
      mainText:
        "Att flytta i Göteborg innebär ofta speciella utmaningar. Landshövdingehusen i Majorna, Haga och Linnéstaden har smala trapphus helt i trä, med låg takhöjd och trånga avsatser. Äldre stenhus i Vasastan och Lorensberg har eleganta men trånga trapphus. Miljonprogramshus i Angered, Bergsjön och Kortedala har breda trapphus men hissar som sällan rymmer stora möbler. Och i villaområden i Örgryte, Hovås och Askim finns branta invändiga trappor och källare som ska tömmas.\n\nMed Ungdomskrafts flytthjälp i Göteborg får du ett team av starka ungdomar och en skåpbil som kör hela sträckan – från gamla bostaden till den nya. Vi packar det som inte redan är packat, demonterar möbler som behöver tas isär, bär ut allt genom Göteborgs alla typer av trapphus, lastar skåpbilen smart och säkert och kör till nya adressen. Sedan bär vi in allt, ställer möblerna där du vill ha dem och monterar ihop det som behöver skruvas.\n\nVi planerar varje flytt utifrån Göteborgs förutsättningar: spårvagnsspår som begränsar parkering i centrum, bästa rutten via E6, E45 och Götaleden, och vilken tid på dagen som minimerar tid i trafiken. Våra team känner stadsdelarna och vet vilka landshövdingehustrappor som kräver extra skydd, var hissproblem är vanliga och hur man bäst navigerar branta backar i Guldheden och Johanneberg.",
      preparationHeading: "Förbered inför din flytt i Göteborg",
      preparationText:
        "Packa så mycket som möjligt i kartonger innan vi kommer – det sparar tid och pengar. Märk kartongerna med rum (kök, sovrum, badrum) så vi kan ställa allt på rätt plats direkt. Demontera möbler som inte går genom dörrar hela: IKEA-garderober, sängramar och stora matbord. Töm kylskåp och frys minst ett dygn innan.\n\nI centrala Göteborg, särskilt kring Avenyn, Haga och Linnégatan, kan parkering vara utmanande – om du har tillgång till innergård, garageinfart eller lastzon, meddela oss vid bokningen. Ha portkod och nycklar redo för båda adresserna och informera grannar om att det kan bli lite spring i trapphuset.",
      additionalSections: [
        {
          heading: "Studentflytt i Göteborg",
          content:
            "Göteborg har tiotusentals studenter som byter boende varje termin – från korridor på Olofshöjd till etta i Haga, från studentrum på Lindholmen till delad tvåa i Mölndal. Vår studentflytt-lösning i Göteborg är anpassad för mindre bohag: ett team på två personer och en skåpbil. Perfekt vid kontraktsbyten, examensflyttar och lägenhetsbyte. Vi packar, bär och kör – du behöver inte göra annat än att öppna dörren. Fast pris som du ser redan vid bokning.",
          cta: { text: "Se bärhjälp flytt →", href: "/barhjalp-flytt" },
        },
        {
          heading: "Familjeflytt i Göteborg – villa, radhus och stor lägenhet",
          content:
            "Vid en familjeflytt i Göteborg skalar vi upp teamet. En fyra i Linnéstaden med fullt bohag, en villa i Örgryte med källare och garage, eller ett radhus i Mölndal med trädgårdsmöbler – vi anpassar antal bärare och fordon efter ditt behov. Vi gör flera vändor om det krävs och planerar rutten så att flytten går effektivt. Tunga vitvaror kopplas loss och ansluts på nya adressen. Vi tar hand om allt från kassaskåp och vitvaror till barnens cyklar och trampolin.",
        },
        {
          heading: "Stadsdelar och kommuner vi flyttar i",
          content:
            "Vi utför flytthjälp i hela Göteborgsregionen. I centrala Göteborg (Linnéstaden, Haga, Masthugget, Johanneberg, Vasastan) navigerar vi trånga landshövdingehustrappor och branta backar med rutin. På Hisingen (Lundby, Backa, Torslanda, Biskopsgården, Eriksberg) och i östra Göteborg (Kortedala, Gamlestaden, Angered, Örgryte) når vi snabbt fram via Götaleden, E6 och E45.\n\nSamma gäller för kranskommuner som Mölndal, Partille, Kungälv, Lerum, Kungsbacka, Härryda och Ale – vi kör utan extra avgifter. Flyttar du mellan kommuner, till exempel från Mölndal till Kungälv? Inga problem – vi kör tvärs genom regionen.\n\nFlytthjälp är en RUT-berättigad tjänst. Du får avdrag på 50 % av arbetskostnaden – upp till 75 000 kr per person och år. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka flytthjälp i Göteborg med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi har flyttat tusentals göteborgare och vet exakt vilka utmaningar varje stadsdel medför – från landshövdingehustrappor i Majorna till villakällare i Örgryte och studentkorridorer på Lindholmen.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Göteborgsregionen. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för centrala Göteborg och inga offertförfrågningar.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla flytt är försäkrade genom Trygg Hansa. Dina ägodelar, trapphuset och fastigheten är skyddade under hela flytten.",
            },
            {
              label: "Allt ingår –",
              text: "Skåpbil, personal, bärning i trappor, möbelfiltar, bärremmar och spännband – allt ingår. Du behöver inte lyfta ett finger.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar flytthjälp i Göteborg?",
        answer:
          "Priset beror på mängden bohag, avstånd och antal bärare. En studentflytt från en etta landar ofta på 995–2 495 kr. En familjeflytt från en trea kan landa på 2 495–4 995 kr. Du ser priset direkt vid bokning – inga dolda avgifter.",
      },
      {
        question: "Kan jag få RUT-avdrag på flytthjälp i Göteborg?",
        answer:
          "Självklart, flytthjälp ger rätt till RUT-avdrag. Du får avdrag på 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Avdraget gäller arbete i eller i nära anslutning till din bostad. Vi hanterar RUT-avdraget direkt på fakturan, så du betalar bara halva arbetskostnaden.",
      },
      {
        question: "Hur snabbt kan ni komma med flytthjälp i Göteborg?",
        answer:
          "Vi har skåpbilar och personal i hela Göteborgsregionen och kan ofta vara på plats redan samma dag. Boka online och se tillgängliga tider direkt.",
      },
      {
        question: "Ingår skåpbil i flytthjälpen?",
        answer:
          "Ja, skåpbil ingår alltid. Vi väljer rätt storlek utifrån ditt bohag – du behöver inte hyra bil, köra eller tanka.",
      },
      {
        question: "Kan ni flytta vitvaror som tvättmaskin och kylskåp?",
        answer:
          "Ja, vi flyttar alla typer av vitvaror. Vi kopplar loss och säkrar dem för transport, och kan ansluta dem igen på nya adressen. Töm kylskåp och frys minst ett dygn innan flytten.",
      },
      {
        question: "Bär ni i trappor utan hiss i Göteborg?",
        answer:
          "Ja, vi bär regelbundet i hisslösa trapphus – ofta tre till fyra våningar. Vi har stor erfarenhet av Göteborgs alla trapptyper: trånga landshövdingehustrappor, stenhusens svängda trappor och miljonprogramstrappor.",
      },
      {
        question: "Hur många bärare skickar ni?",
        answer:
          "Vi anpassar teamet efter din flytt. En etta eller tvåa behöver oftast två bärare. En trea eller fyra med mycket möbler kan kräva tre till fyra personer. Ange vad som ska flyttas vid bokning.",
      },
      {
        question: "Skyddar ni möblerna under flytten?",
        answer:
          "Ja, vi har möbelfiltar, bärremmar, skyddsmattor och spännband. Vi skyddar golv, väggar och dörrkarmar vid ut- och inbärning. Allt är försäkrat genom Trygg Hansa.",
      },
      {
        question: "Kan ni hjälpa med flytt på helger?",
        answer:
          "Ja, vi erbjuder flytthjälp alla dagar i veckan, inklusive lördagar och söndagar. Många kunder föredrar helgflytt för att slippa ta ledigt från jobbet.",
      },
      {
        question: "Demonterar och monterar ni möbler?",
        answer:
          "Ja, vi demonterar garderober, sängramar, bord och hyllor som behöver tas isär. På nya adressen monterar vi ihop allt igen. Spara gärna skruvar och beslag i märkta påsar.",
      },
      {
        question: "Kan ni göra flera vändor under samma flytt?",
        answer:
          "Ja, vid stor flytt – till exempel villa med källare och garage – gör vi flera vändor med skåpbilen. Vi planerar antalet vändor i förväg baserat på ditt bohags storlek.",
      },
      {
        question: "Erbjuder ni kontorsflytt i Göteborg?",
        answer:
          "Ja, vi utför kontorsflytt i hela Göteborgsregionen – från enskilda arbetsplatser på Lindholmen till hela kontor i centrum. Vi hanterar skrivbord, kontorsstolar, arkivskåp och IT-utrustning.",
      },
      {
        question: "Kan jag få flytthjälp samma vecka?",
        answer:
          "Ja, vi har ofta lediga tider med kort varsel. Många kunder bokar flytt redan för samma vecka. Boka online och se direkt vilka tider som är tillgängliga.",
      },
      {
        question: "Vilka stadsdelar i Göteborg täcker ni?",
        answer:
          "Vi utför flytthjälp i hela Göteborg – centrum (Linnéstaden, Haga, Masthugget, Johanneberg, Vasastan), Hisingen (Lundby, Backa, Torslanda, Eriksberg), östra Göteborg (Kortedala, Gamlestaden, Örgryte) och alla övriga stadsdelar.",
      },
      {
        question: "Vilka kommuner runt Göteborg erbjuder ni flytthjälp i?",
        answer: `Vi erbjuder flytthjälp i hela Göteborgsregionen: ${göteborgKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.transport,

    pricingRows: getPackagePricingRows("transport", "sv"),

    dynamicHeadings: {
      threeSteps: "Flytthjälp i Göteborg i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Göteborg packar, bär ut allt, lastar skåpbilen, kör till nya adressen och bär in allt på rätt plats. Du behöver inte lyfta ett finger – vi sköter hela flytten.",
      step3Title: "Allt på plats",
      step3Desc:
        "Vi lossar och bär in allt i ditt nya hem. Möbler ställs på rätt plats och det som demonterats monteras ihop igen. Du kan börja leva i nya hemmet direkt.",
      ctaHeading: "Boka flytthjälp i Göteborg idag",
      ctaText:
        "Ange gamla och nya adressen, beskriv ditt bohag och välj en tid. Du ser priset direkt – skåpbil och personal ingår.",
    },

    en: {
      slug: "moving-help-gothenburg",
      meta: {
        title: "Moving Help Gothenburg – Van & Crew | Ungdomskraft",
        description:
          "Book moving help in Gothenburg – we pack, carry, drive and unload. A complete door-to-door move across the Gothenburg region. Fixed price, often same day.",
        h1: "Moving Help Gothenburg",
      },
      hero: {
        heading: "Moving Help in Gothenburg",
        intro:
          "Book moving help in Gothenburg — we handle the entire move with a van and a team of strong, committed young workers. From student moves in Haga to family moves in Örgryte. We pack, carry, drive and place everything in your new home. Fixed price, quick booking and often available the same day.",
      },
      featureCards: {
        sinceText: "Moving help since 2014",
        bookYourText: "Book your move",
        bookDescText:
          "Enter the old and new address, describe what needs moving and pick a time. You see the price instantly — everything is included.",
        vehicleTitleText: "Van and crew in one package",
        vehicleDescText:
          "No need to rent a vehicle or organise transport — we arrive with a van and a full team. We load, drive and unload. Big, small, heavy or bulky — we have the right vehicle and enough arms.",
      },
      itemsSection: {
        heading: "Common items we move in Gothenburg",
        items: [
          {
            name: "Sofas & armchairs",
            description: "Corner sofas, sofa beds and heavy armchairs — protected and carried",
            image: "/sofa.webp",
          },
          {
            name: "Beds & mattresses",
            description: "Double beds, continental beds and heavy mattresses",
            image: "/bed.webp",
          },
          {
            name: "Tables & chairs",
            description: "Dining tables, desks, chairs and dining sets",
            image: "/table.webp",
          },
          {
            name: "Appliances",
            description: "Washing machines, tumble dryers, fridges and dishwashers",
            image: "/washer.webp",
          },
          {
            name: "Wardrobes & dressers",
            description: "PAX wardrobes, dressers, bookcases and shelving units",
            image: "/wardrobe.webp",
          },
          {
            name: "Moving boxes",
            description: "Packed boxes with books, clothes, kitchen items and belongings",
            image: "/box.webp",
          },
        ],
      },
      seoContent: {
        mainHeading: "Complete moving help in Gothenburg — van, crew and everything in between",
        mainText:
          "Moving in Gothenburg often comes with unique challenges. The landshövdingehus in Majorna, Haga and Linnéstaden have narrow wooden stairwells with low ceilings and tight landings. Older stone buildings in Vasastan and Lorensberg have elegant but narrow stairwells. Million Programme buildings in Angered, Bergsjön and Kortedala have wide stairwells but lifts that rarely fit large furniture. And residential areas in Örgryte, Hovås and Askim have steep internal staircases and basements to clear.\n\nWith Ungdomskraft's moving help in Gothenburg you get a team of strong young workers and a van that drives the whole distance — from the old home to the new one. We pack what isn't already packed, disassemble furniture that needs taking apart, carry everything out through Gothenburg's many types of stairwells, load the van smartly and securely and drive to the new address. Then we carry everything in, place furniture where you want it and reassemble anything that needs putting together.\n\nWe plan every move based on Gothenburg's conditions: tram tracks limiting parking in the centre, the best route via the E6, E45 and Götaleden, and what time of day minimises time in traffic. Our teams know the neighborhoods and understand which landshövdingehus stairwells need extra protection, where lift problems are common and how to navigate steep hills in Guldheden and Johanneberg.",
        preparationHeading: "Prepare for your move in Gothenburg",
        preparationText:
          "Pack as much as possible into boxes before we arrive — it saves time and money. Label boxes by room (kitchen, bedroom, bathroom) so we can put everything in the right place straight away. Disassemble furniture that won't fit through doors whole: IKEA wardrobes, bed frames and large dining tables. Empty the fridge and freezer at least a day before.\n\nIn central Gothenburg, especially around Avenyn, Haga and Linnégatan, parking can be challenging — if you have access to a courtyard, garage entrance or loading zone, let us know when booking. Have entry codes and keys ready for both addresses and let neighbours know there may be some traffic in the stairwell.",
        additionalSections: [
          {
            heading: "Student moves in Gothenburg",
            content:
              "Gothenburg has tens of thousands of students who switch housing every semester — from a corridor room at Olofshöjd to a studio in Haga, from a student room at Lindholmen to a shared flat in Mölndal. Our student-move solution in Gothenburg is built for smaller households: a team of two and a van. Perfect for lease changes, graduation moves and flat swaps. We pack, carry and drive — all you need to do is open the door. Fixed price that you see when booking.",
            cta: { text: "Carrying help for moving →", href: "/en/carrying-help-move" },
          },
          {
            heading: "Family moves in Gothenburg — houses, terraces and large flats",
            content:
              "For a family move in Gothenburg we scale the team up. A four-room flat in Linnéstaden with a full household, a house in Örgryte with a basement and garage, or a terraced house in Mölndal with garden furniture — we adapt the number of carriers and vehicle to your needs. We make multiple trips if necessary and plan the route so the move runs efficiently. Heavy appliances are disconnected and reconnected at the new address. We handle everything from safes and appliances to children's bikes and trampolines.",
          },
          {
            heading: "Neighborhoods and municipalities we move in",
            content:
              "We provide moving help across the entire Gothenburg region. In central Gothenburg (Linnéstaden, Haga, Masthugget, Johanneberg, Vasastan) we navigate narrow landshövdingehus stairwells and steep hills with routine. On Hisingen (Lundby, Backa, Torslanda, Biskopsgården, Eriksberg) and in eastern Gothenburg (Kortedala, Gamlestaden, Angered, Örgryte) we reach you quickly via Götaleden, the E6 and E45.\n\nThe same goes for surrounding municipalities like Mölndal, Partille, Kungälv, Lerum, Kungsbacka, Härryda and Ale — no extra charges. Moving between municipalities, for example from Mölndal to Kungälv? No problem — we drive across the entire region.\n\nMoving help is eligible for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost – up to SEK 75,000 per person per year. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book moving help in Gothenburg with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We have moved thousands of Gothenburg residents and know exactly what challenges each neighborhood presents — from landshövdingehus stairs in Majorna to villa basements in Örgryte and student corridors at Lindholmen.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Gothenburg region. We can often be on site the same day you book — including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for central Gothenburg and no quote requests.",
              },
              {
                label: "Insured and safe –",
                text: "All moves are insured through Trygg Hansa. Your belongings, stairwell and property are protected throughout the move.",
              },
              {
                label: "Everything included –",
                text: "Van, crew, stair carrying, furniture blankets, straps and tie-downs — everything is included. You don't have to lift a finger.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does moving help in Gothenburg cost?",
          answer:
            "The price depends on the volume of belongings, distance and number of carriers. A student move from a studio often costs SEK 995–2,495. A family move from a two- or three-bed can cost SEK 2,495–4,995. You see the price instantly when booking — no hidden fees.",
        },
        {
          question: "Can I get a RUT tax deduction on moving help in Gothenburg?",
          answer:
            "Of course, moving help qualifies for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost, up to SEK 75,000 per person per year. The deduction covers work in or near your home. We handle the RUT deduction directly on the invoice, so you only pay half the labour cost.",
        },
        {
          question: "How quickly can you come with moving help in Gothenburg?",
          answer:
            "We have vans and staff across the Gothenburg region and can often be on site the same day. Book online and see available times instantly.",
        },
        {
          question: "Is a van included?",
          answer:
            "Yes, a van is always included. We choose the right size based on your household — no need to rent a vehicle, drive or refuel.",
        },
        {
          question: "Can you move appliances like washing machines and fridges?",
          answer:
            "Yes, we move all types of appliances. We disconnect and secure them for transport, and can reconnect them at the new address. Remember to empty the fridge and freezer at least a day before.",
        },
        {
          question: "Do you carry up stairs without a lift in Gothenburg?",
          answer:
            "Yes, we regularly carry in buildings without lifts — often three to four floors. We have extensive experience with all of Gothenburg's stairwell types: narrow landshövdingehus stairs, stone building stairwells and Million Programme stairs.",
        },
        {
          question: "How many carriers do you send?",
          answer:
            "We adapt the team to your move. A studio or one-bed usually needs two carriers. A two- or three-bed with lots of furniture may need three to four. Describe what needs moving when booking.",
        },
        {
          question: "Do you protect furniture during the move?",
          answer:
            "Yes, we bring furniture blankets, carrying straps, floor mats and ratchet straps. We protect floors, walls and door frames when carrying out and in. Everything is insured through Trygg Hansa.",
        },
        {
          question: "Can you help with moving on weekends?",
          answer:
            "Yes, we offer moving help every day of the week, including Saturdays and Sundays. Many customers prefer a weekend move so they don't have to take time off work.",
        },
        {
          question: "Do you disassemble and reassemble furniture?",
          answer:
            "Yes, we disassemble wardrobes, bed frames, tables and shelves that need taking apart. At the new address we reassemble everything. It helps to keep screws and fittings in labelled bags.",
        },
        {
          question: "Can you make multiple trips during the same move?",
          answer:
            "Yes, for larger moves — for example a house with basement and garage — we make multiple trips with the van. We plan the number of trips in advance based on the size of your household.",
        },
        {
          question: "Do you offer office moves in Gothenburg?",
          answer:
            "Yes, we carry out office moves across the Gothenburg region — from individual workstations at Lindholmen to entire offices in the city centre. We handle desks, office chairs, filing cabinets and IT equipment.",
        },
        {
          question: "Can I get moving help the same week?",
          answer:
            "Yes, we often have availability at short notice. Many customers book a move for the same week. Book online and see straight away which times are available.",
        },
        {
          question: "Which neighborhoods in Gothenburg do you cover?",
          answer:
            "We provide moving help across all of Gothenburg — the city centre (Linnéstaden, Haga, Masthugget, Johanneberg, Vasastan), Hisingen (Lundby, Backa, Torslanda, Eriksberg), eastern Gothenburg (Kortedala, Gamlestaden, Örgryte) and all other neighborhoods.",
        },
        {
          question: "Which municipalities around Gothenburg do you serve?",
          answer:
            "We provide moving help across the entire Gothenburg region: Ale, Alingsås, Göteborg, Härryda, Kungälv, Kungsbacka, Lerum, Lilla Edet, Mölndal, Partille, Stenungsund, Tjörn and Öckerö. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.transport,
      pricingRows: getPackagePricingRows("transport", "en"),
      dynamicHeadings: {
        threeSteps: "Moving help in Gothenburg in 3 easy steps",
        step2Text:
          "Our workers in Gothenburg pack, carry everything out, load the van, drive to the new address and carry everything in. You don't need to lift a finger — we handle the entire move.",
        step3Title: "Everything in place",
        step3Desc:
          "We unload and carry everything into your new home. Furniture is placed where you want it and anything disassembled is reassembled. You can start living in your new home straight away.",
        ctaHeading: "Book moving help in Gothenburg today",
        ctaText:
          "Enter the old and new address, describe your household and pick a time. You see the price instantly — van and crew included.",
      },
    },
  },

  // ─── Möbelmontering Göteborg ──────────────────────────────────────
  {
    slug: "mobelmontering-goteborg",
    serviceCategory: "möbelmontering",
    city: "göteborg",

    meta: {
      title: "Möbelmontering Göteborg – Snabb & Prisvärd | Ungdomskraft",
      description:
        "Boka möbelmontering i Göteborg. Vi monterar IKEA-möbler, garderober, kök och plattpack i alla stadsdelar – Majorna, Hisingen, Linnéstaden och fler. Samma dag!",
      h1: "Möbelmontering Göteborg",
    },

    hero: {
      heading: "Möbelmontering i Göteborg",
      intro:
        "Boka möbelmontering i Göteborg – vi skickar montörer med komplett verktygslåda till alla stadsdelar och kranskommuner. Från en enkel bokhylla till ett helt IKEA-kök. Fast pris, enkel bokning och ofta tillgängliga redan samma dag.",
    },

    featureCards: {
      headingText: "Vi monterar dina möbler i Göteborg",
      sinceText: "Möbelmontering sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft monterat tusentals plattpackade möbler åt göteborgare – från ettor i Majorna till radhus i Mölndal. Vårt team av snabba ungdomar bygger allt med den noggrannhet och energi som krävs för ett perfekt resultat.",
      bookYourText: "Boka din möbelmontering",
      bookDescText:
        "Beskriv vilka möbler som ska monteras, ange din adress i Göteborg och se priset direkt. Vi tar med alla verktyg.",
      vehicleTitleText: "Bil med komplett verktygslåda",
      vehicleDescText:
        "Våra montörer i Göteborg kommer med bil utrustad med komplett verktygslåda: borrmaskin, skruvdragare, insexnycklar i alla storlekar, vattenpass, gummiklubba och tumstock. Du behöver inte ha några egna verktyg hemma.",
    },

    itemsSection: {
      heading: "Möbler vi monterar i Göteborg",
      items: [
        {
          name: "Bokhyllor & hyllsystem",
          description:
            "BILLY, KALLAX, EKET och andra bokhyllor och förvaringssystem",
          image: "/wardrobe.webp",
        },
        {
          name: "Garderober",
          description:
            "PAX, PLATSA och andra garderobssystem med skjutdörrar, lådor och inredning",
          image: "/wardrobe.webp",
        },
        {
          name: "Skrivbord & kontorsmöbler",
          description:
            "Skrivbord, höj- och sänkbara bord, kontorsstolar och hurtsar",
          image: "/office-desk.webp",
        },
        {
          name: "Sängar & sängstommar",
          description:
            "Sängstommar, loftsängar, våningssängar och huvudgavlar",
          image: "/bed.webp",
        },
        {
          name: "Matbord & stolar",
          description:
            "Matgrupper, utdragbara bord, bänkar och stolar som kräver montering",
          image: "/table.webp",
        },
        {
          name: "Köksskåp & bänkskivor",
          description:
            "METOD-kök, överskåp, underskåp, bänkskivor och köksöar",
          image: "/dishes.webp",
        },
      ],
    },

    seoContent: {
      mainHeading: "Möbelmontering i Göteborg – från plattpack till färdig möbel",
      mainText:
        "Göteborg är en stad där många bor i äldre landshövdingehus med trånga trapphus, men också i moderna lägenheter med öppna planlösningar och stora garderobssystem. Oavsett var du bor skickar vi montörer med komplett verktygslåda direkt till din dörr. Har du handlat på IKEA Bäckebol, IKEA Kållered eller fått en hemleverans från JYSK eller Mio? Vi monterar allt från enstaka bokhyllor till kompletta garderobssystem och hela kök.\n\nGöteborgs bostäder ställer varierande krav. Landshövdingehus i Majorna och Linnéstaden har ofta smala trappor och högt i tak, vilket kräver erfarenhet och rätt verktyg. Nybyggda lägenheter i Eriksberg och Lindholmen har moderna planlösningar med walk-in closets och skräddarsydda förvaringslösningar. Radhus i Mölndal, Härryda och Kungsbacka har ofta garage med plattpackade möbler som väntar på montering. Vårt team har erfarenhet av alla dessa förutsättningar och vet hur man jobbar effektivt i Göteborgs varierande bostadsbestånd.\n\nVi tar med borrmaskin, skruvdragare, insexnycklar i alla storlekar, vattenpass, gummiklubba, tumstock och allt annat som behövs. Du behöver inte ens ha en skruvmejsel hemma.",
      preparationHeading: "Förbered inför möbelmonteringen i Göteborg",
      preparationText:
        "Se till att kartongerna med de plattpackade möblerna finns i rätt rum innan montörerna kommer. Om du bor i ett landshövdingehus i Majorna eller Linnéstaden och kartongerna är tunga, kan vi hjälpa till att bära upp dem – nämn det i bokningen. Kontrollera gärna att alla delar finns med genom att stämma av mot innehållsförteckningen. Rensa golvet så att det finns tillräckligt med arbetsyta. Om du har specifika önskemål om placering, markera gärna var möbeln ska stå.",
      additionalSections: [
        {
          heading: "Stadsdelar och kommuner vi täcker i Göteborg",
          content:
            "Vi utför möbelmontering i hela Göteborgsregionen – från centrum till ytterområden och kranskommuner. I centrala Göteborg (Linnéstaden, Haga, Vasastan, Johanneberg, Örgryte) har vi stor erfarenhet av äldre byggnader med trånga trappor och begränsade hissar. Vi planerar alltid logistiken i förväg.\n\nPå Hisingen (Lundby, Eriksberg, Torslanda, Backa, Biskopsgården) når vi er snabbt. Samma gäller för södra stadsdelarna (Högsbo, Frölunda, Tynnered, Askim) och kranskommuner som Mölndal, Partille, Kungälv, Kungsbacka, Härryda och Lerum. Nybyggda områden som Eriksberg, Lindholmen och Kvillebäcken har ofta stora lägenheter med omfattande förvaring som kräver montering – perfekt uppdrag för oss.",
        },
        {
          heading: "Kombinera hämtning och montering i Göteborg",
          content:
            "Handlat på IKEA Bäckebol eller IKEA Kållered och saknar bil? Vi hämtar de plattpackade kartongerna direkt i butiken, kör hem dem till din adress i Göteborg och monterar allt på plats. Samma sak gäller för JYSK, Mio, Rusta och andra möbelbutiker i Göteborgsområdet. Allt i en enda bokning – från butikshylla till färdig möbel i ditt hem. Du slipper hyra släp, bära tunga kartonger i trappor och sedan montera allt själv.",
        },
        {
          heading: "Möbelmontering för företag och kontor i Göteborg",
          content:
            "Flyttar ditt företag in i nya lokaler på Lindholmen, i Gamlestaden eller i Mölndal? Vi monterar kontorsmöbler i alla storlekar – från enskilda höj- och sänkbara skrivbord till kompletta mötesrumsuppsättningar och öppna kontorslandskap. Vi kan arbeta utanför kontorstid, på kvällar och helger, så att monteringen inte stör den dagliga verksamheten. Företag som anlitar oss kan inkludera våra uppdrag i sin CSR-rapportering, eftersom varje bokning sysselsätter unga göteborgare och bidrar till ungdomars väg in på arbetsmarknaden.",
        },
        {
          heading: "Varför boka möbelmontering i Göteborg med Ungdomskraft?",
          content:
            "Ungdomskraft finns i Göteborg med samma vision som när vi startade 2014: att ge unga människor en riktig chans på arbetsmarknaden, samtidigt som kunder får snabb och prisvärd hjälp. Vi har genomfört tusentals uppdrag i Göteborgsregionen och sysselsatt hundratals ungdomar.\n\nVåra montörer är unga, läraktiga och genuint motiverade. De har gått igenom vår interna utbildning där de lär sig allt från att läsa monteringsinstruktioner effektivt till att hantera verktyg säkert och professionellt. Varje uppdrag i Göteborg är en chans att utvecklas, och det märks i kvaliteten på arbetet. De tar sig tid att göra rätt – justerar skjutdörrar tills de glider perfekt, kontrollerar att hyllplan sitter i våg och ser till att lådor öppnas och stängs utan motstånd.\n\nFör många av våra ungdomar i Göteborg är Ungdomskraft det första riktiga jobbet. Här lär de sig kundkontakt, punktlighet, problemlösning och att ta ansvar för ett färdigt resultat. Det är arbetslivserfarenhet som Volvo, SKF, banker och andra arbetsgivare i regionen värderar högt. Genom att boka möbelmontering med oss investerar du inte bara i färdiga möbler – du investerar i en ung göteborgares framtid.\n\nVi tar kvalitet på allvar. Varje uppdrag avslutas med en genomgång där montören kontrollerar att alla skruvar sitter, att dörrar stänger ordentligt och att möbeln står stadigt. Om något inte känns rätt fixar vi det på plats.\n\nHållbarhet är en naturlig del av vårt arbete. Vi tar hand om allt emballage och sorterar kartong, plast och frigolit för återvinning. Att boka möbelmontering med Ungdomskraft i Göteborg är mer än en tjänst – det är hjälp med mening.\n\nMöbelmontering är en RUT-berättigad tjänst. Du får avdrag på 50 % av arbetskostnaden – upp till 75 000 kr per person och år. Avdraget hanteras direkt på fakturan.",
        },
      ],
    },

    faq: [
      {
        question: "Vilka verktyg tar ni med till möbelmontering i Göteborg?",
        answer:
          "Vi tar med komplett verktygslåda: borrmaskin, skruvdragare, insexnycklar i alla storlekar, vattenpass, gummiklubba, tumstock, hammare och allt annat som behövs. Du behöver inte ha några egna verktyg.",
      },
      {
        question: "Monterar ni IKEA-möbler i Göteborg?",
        answer:
          "Ja, IKEA-möbler är vår vanligaste möbelmontering i Göteborg. Vi har monterat tusentals PAX-garderober, BILLY-bokhyllor, KALLAX-hyllor och METOD-kök åt göteborgare. Vi känner igen varje serie och vet exakt hur de ska byggas.",
      },
      {
        question: "Kan ni montera möbler i Göteborg samma dag?",
        answer:
          "Ja, vi erbjuder möbelmontering samma dag i Göteborg om du bokar tidigt. Våra montörer kan ofta vara på plats inom några timmar, oavsett om du bor i centrum eller i en kranskommun.",
      },
      {
        question: "Vad kostar möbelmontering i Göteborg?",
        answer:
          "Priset beror på antal möbler och komplexitet. En enkel möbel från 595 kr (Extra Liten). En garderob eller ett par möbler från 995 kr (Small). Flera möbler från 1 495 kr (Medium). Du ser alltid det uppskattade priset innan du bokar.",
      },
      {
        question: "Kan jag få RUT-avdrag på möbelmontering i Göteborg?",
        answer:
          "Självklart, möbelmontering ger rätt till RUT-avdrag. Du får avdrag på 50 % av arbetskostnaden, upp till 75 000 kr per person och år. Avdraget gäller arbete i eller i nära anslutning till din bostad. Vi hanterar RUT-avdraget direkt på fakturan, så du betalar bara halva arbetskostnaden.",
      },
      {
        question: "Kan ni hämta möblerna på IKEA och montera hemma i Göteborg?",
        answer:
          "Ja, vi hämtar plattpackade möbler från IKEA Bäckebol, IKEA Kållered eller annan butik i Göteborg, kör hem dem till dig och monterar allt på plats – i en enda bokning.",
      },
      {
        question: "Monterar ni möbler i alla Göteborgs stadsdelar?",
        answer:
          "Ja, vi monterar möbler i hela Göteborg – Majorna, Linnéstaden, Haga, Hisingen, Eriksberg, Högsbo, Frölunda och alla andra stadsdelar. Vi täcker även kranskommuner som Mölndal, Partille, Kungsbacka, Kungälv och Härryda.",
      },
      {
        question: "Är mina möbler försäkrade under monteringen i Göteborg?",
        answer:
          "Ja, alla uppdrag är försäkrade. Om något skulle gå sönder under möbelmonteringen ersätter vi det. Våra montörer arbetar noggrant och varsamt med dina möbler.",
      },
      {
        question: "Tar ni hand om förpackningsmaterialet i Göteborg?",
        answer:
          "Ja, vi plockar ihop all kartong, cellplast och frigolit efter monteringen. Vi kan ta med oss allt emballage så slipper du springa till återvinningen.",
      },
      {
        question: "Kan ni montera kök i Göteborg?",
        answer:
          "Ja, vi monterar köksskåp, bänkskivor och köksöar i hela Göteborgsområdet. Vi hanterar allt snickeri – underskåp, överskåp, luckor, lådor och beslag. El- och VVS-anslutningar beställer du separat av behörig installatör.",
      },
      {
        question: "Monterar ni kontorsmöbler i Göteborg?",
        answer:
          "Ja, vi monterar kontorsmöbler i hela Göteborg – höj- och sänkbara skrivbord, kontorsstolar, hurtsar och mötesrumsmöbler. Vi kan arbeta på kvällar och helger så att monteringen inte stör verksamheten.",
      },
      {
        question: "Hur lång tid tar möbelmontering av en PAX-garderob i Göteborg?",
        answer:
          "En standard PAX-garderob med två dörrar tar cirka 45–90 minuter. Större system med skjutdörrar, lådor och inredning kan ta 2–3 timmar. Vi meddelar uppskattad tid vid bokning.",
      },
      {
        question: "Kan ni demontera möbler i Göteborg också?",
        answer:
          "Ja, vi både monterar och demonterar möbler i Göteborg. Perfekt om du ska flytta och behöver ta isär garderober, sängar eller skrivbord innan flytten.",
      },
      {
        question: "Monterar ni möbler på kvällar och helger i Göteborg?",
        answer:
          "Ja, vi erbjuder möbelmontering i Göteborg vardagar, kvällar och helger. Du väljer den tid som passar dig bäst direkt vid bokning.",
      },
      {
        question: "I vilka kommuner i Göteborgsområdet erbjuder ni möbelmontering?",
        answer:
          `Vi erbjuder möbelmontering i hela Göteborgsområdet: ${göteborgKommunerText}. Vi täcker även alla stadsdelar inom Göteborgs stad.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.montering,

    pricingRows: getPackagePricingRows("montering", "sv"),

    dynamicHeadings: {
      threeSteps: "Möbelmontering i Göteborg i 3 enkla steg",
      step2Title: "Vi monterar allt",
      step2Text:
        "Våra montörer i Göteborg kommer med bil och komplett verktygslåda. De packar upp, monterar och kontrollerar att allt sitter perfekt. Kartong och emballage plockas ihop.",
      step3Title: "Klart",
      step3Desc:
        "Dina möbler är monterade, justerade och redo att användas. Emballaget är bortplockat och du kan njuta av resultatet.",
      ctaHeading: "Boka möbelmontering i Göteborg idag",
      ctaText:
        "Beskriv vilka möbler som ska monteras, ange din adress i Göteborg och se priset direkt. Vi tar med alla verktyg – du behöver inte lyfta ett finger.",
    },

    en: {
      slug: "furniture-assembly-gothenburg",
      meta: {
        title: "Furniture Assembly Gothenburg – Fast & Affordable | Ungdomskraft",
        description:
          "Book furniture assembly in Gothenburg – IKEA, wardrobes, kitchens and all flat-pack. Available across all neighborhoods. Same day service!",
        h1: "Furniture Assembly Gothenburg",
      },

      hero: {
        heading: "Furniture assembly in Gothenburg",
        intro:
          "Book furniture assembly in Gothenburg – we send assemblers with a complete toolbox to all neighborhoods and surrounding municipalities. From a simple bookshelf to a full IKEA kitchen. Fixed price, easy booking and often available the same day.",
      },

      featureCards: {
        headingText: "We assemble your furniture in Gothenburg",
        sinceText: "Furniture assembly since 2014",
        storyText:
          "Since 2014, Ungdomskraft has assembled thousands of flat-pack furniture pieces for Gothenburg residents – from studios in Majorna to townhouses in Mölndal. Our team of quick young assemblers builds everything with the precision and energy required for a perfect result.",
        bookYourText: "Book your assembly",
        bookDescText:
          "Describe what furniture needs assembling, enter your Gothenburg address and see the price instantly. We bring all tools.",
        vehicleTitleText: "Car with a complete toolbox",
        vehicleDescText:
          "Our assemblers in Gothenburg arrive by car equipped with a complete toolbox: power drill, screwdriver, Allen keys in all sizes, spirit level, rubber mallet and tape measure. You don't need any tools at home.",
      },

      itemsSection: {
        heading: "Furniture we assemble in Gothenburg",
        items: [
          {
            name: "Bookshelves & storage",
            description: "BILLY, KALLAX, EKET and other bookshelves and storage systems",
            image: "/wardrobe.webp",
          },
          {
            name: "Wardrobes",
            description: "PAX, PLATSA and other wardrobe systems with sliding doors, drawers and fittings",
            image: "/wardrobe.webp",
          },
          {
            name: "Desks & office furniture",
            description: "Desks, sit-stand desks, office chairs and drawer units",
            image: "/office-desk.webp",
          },
          {
            name: "Beds & bed frames",
            description: "Bed frames, loft beds, bunk beds and headboards",
            image: "/bed.webp",
          },
          {
            name: "Dining tables & chairs",
            description: "Dining sets, extendable tables, benches and chairs requiring assembly",
            image: "/table.webp",
          },
          {
            name: "Kitchen cabinets & countertops",
            description: "METOD kitchens, wall cabinets, base cabinets, countertops and kitchen islands",
            image: "/dishes.webp",
          },
        ],
      },

      seoContent: {
        mainHeading: "Furniture assembly in Gothenburg – from flat-pack to finished furniture",
        mainText:
          "Gothenburg is a city where many people live in older landshövdingehus with narrow staircases, but also in modern apartments with open floor plans and large wardrobe systems. Wherever you live, we send assemblers with a complete toolbox straight to your door. Shopped at IKEA Bäckebol, IKEA Kållered or received a home delivery from JYSK or Mio? We assemble everything from single bookshelves to complete wardrobe systems and entire kitchens.\n\nGothenburg homes present varying assembly demands. Landshövdingehus in Majorna and Linnéstaden often have narrow stairs and high ceilings, which requires experience and the right tools. New-build apartments in Eriksberg and Lindholmen have modern floor plans with walk-in closets and custom storage solutions. Townhouses in Mölndal, Härryda and Kungsbacka often have garages full of flat-pack furniture waiting to be assembled. Our team has experience with all these conditions and knows how to work efficiently across Gothenburg's varied housing stock.\n\nWe bring power drills, screwdrivers, Allen keys in all sizes, spirit levels, rubber mallets, tape measures and everything else needed. You don't even need a screwdriver at home.",
        preparationHeading: "How to prepare for assembly in Gothenburg",
        preparationText:
          "Make sure the flat-pack boxes are in the right room before the assemblers arrive. If you live in a landshövdingehus in Majorna or Linnéstaden and the boxes are heavy, we can help carry them up – mention it when booking. Check that all parts are included by reviewing the contents list. Clear the floor to provide workspace. If you have specific placement preferences, mark where the furniture should go.",
        additionalSections: [
          {
            heading: "Neighborhoods and municipalities we cover in Gothenburg",
            content:
              "We perform furniture assembly across the entire Gothenburg region – from the city centre to the outskirts and surrounding municipalities. In central Gothenburg (Linnéstaden, Haga, Vasastan, Johanneberg, Örgryte), we have extensive experience with older buildings with narrow stairs and limited lifts.\n\nOn Hisingen (Lundby, Eriksberg, Torslanda, Backa, Biskopsgården) we reach you quickly. The same applies to the southern districts (Högsbo, Frölunda, Tynnered, Askim) and surrounding municipalities such as Mölndal, Partille, Kungälv, Kungsbacka, Härryda and Lerum. New-build areas like Eriksberg, Lindholmen and Kvillebäcken often have large apartments with extensive storage requiring comprehensive assembly – a perfect job for us.",
          },
          {
            heading: "Combine pickup and assembly in Gothenburg",
            content:
              "Shopped at IKEA Bäckebol or IKEA Kållered and don't have a car? We pick up the flat-pack boxes directly from the store, drive them to your Gothenburg address and assemble everything on site. The same applies for JYSK, Mio, Rusta and other furniture stores in the Gothenburg area. All in one booking – from store shelf to finished furniture in your home. You skip renting a trailer, carrying heavy boxes up stairs and then assembling everything yourself.",
          },
          {
            heading: "Furniture assembly for businesses and offices in Gothenburg",
            content:
              "Is your company moving into new premises on Lindholmen, in Gamlestaden or in Mölndal? We assemble office furniture of all sizes – from individual sit-stand desks to complete meeting room setups and open-plan office landscapes. We can work outside business hours, on evenings and weekends, so the assembly doesn't disrupt daily operations. Companies that hire us can include our jobs in their CSR reporting, since every booking employs young Gothenburgers and contributes to youth employment.",
          },
          {
            heading: "Why book furniture assembly in Gothenburg with Ungdomskraft?",
            content:
              "Ungdomskraft operates in Gothenburg with the same vision as when we started in 2014: to give young people a real chance in the job market while offering customers fast and affordable help. We have completed thousands of jobs in the Gothenburg region and employed hundreds of young people.\n\nOur assemblers are young, eager to learn and genuinely motivated. They have completed our internal training programme where they learn everything from reading assembly instructions efficiently to handling tools safely and professionally. Every job in Gothenburg is a chance to grow, and it shows in the quality of the work. They take the time to get it right – adjusting sliding doors until they glide perfectly, checking that shelves are level and making sure drawers open and close without resistance.\n\nFor many of our young workers in Gothenburg, Ungdomskraft is their first real job. Here they learn customer interaction, punctuality, problem-solving and taking responsibility for a finished result. This is work experience that Volvo, SKF, banks and other regional employers value highly. By booking furniture assembly with us, you are not just investing in finished furniture – you are investing in a young Gothenburger's future.\n\nWe take quality seriously. Every job ends with a walkthrough where the assembler checks that all screws are tight, doors close properly and the furniture stands steady. If anything doesn't feel right, we fix it on the spot.\n\nSustainability is a natural part of our work. We handle all packaging and sort cardboard, plastic and polystyrene for recycling. Booking furniture assembly with Ungdomskraft in Gothenburg is more than a service – it is help with purpose.\n\nFurniture assembly is eligible for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost – up to SEK 75,000 per person per year. The deduction is handled directly on the invoice.",
          },
        ],
      },

      faq: [
        {
          question: "What tools do you bring for furniture assembly in Gothenburg?",
          answer:
            "We bring a complete toolbox: power drill, screwdriver, Allen keys in all sizes, spirit level, rubber mallet, tape measure, hammer and everything else needed. You don't need any tools at home.",
        },
        {
          question: "Do you assemble IKEA furniture in Gothenburg?",
          answer:
            "Yes, IKEA furniture is our most common assembly job in Gothenburg. We have assembled thousands of PAX wardrobes, BILLY bookshelves, KALLAX shelves and METOD kitchens for Gothenburg residents. We know every range and exactly how to build them.",
        },
        {
          question: "Can you assemble furniture in Gothenburg the same day?",
          answer:
            "Yes, we offer same-day furniture assembly in Gothenburg if you book early. Our assemblers can often be there within a few hours, whether you live in the city centre or in a surrounding municipality.",
        },
        {
          question: "How much does furniture assembly cost in Gothenburg?",
          answer:
            "The price depends on the number of pieces and complexity. A single piece from SEK 595 (Extra Liten). A wardrobe or a couple of pieces from SEK 995 (Small). Multiple pieces from SEK 1,495 (Medium). You always see the estimated price before you book.",
        },
        {
          question: "Can I get a RUT tax deduction on furniture assembly in Gothenburg?",
          answer:
            "Of course, furniture assembly qualifies for the Swedish RUT tax deduction. You get a 50% deduction on the labour cost, up to SEK 75,000 per person per year. The deduction covers work in or near your home. We handle the RUT deduction directly on the invoice, so you only pay half the labour cost.",
        },
        {
          question: "Can you pick up furniture from IKEA and assemble it at home in Gothenburg?",
          answer:
            "Yes, we pick up flat-pack furniture from IKEA Bäckebol, IKEA Kållered or another store in Gothenburg, drive it to your home and assemble everything on site – all in one booking.",
        },
        {
          question: "Do you assemble furniture in all Gothenburg neighborhoods?",
          answer:
            "Yes, we assemble furniture across all of Gothenburg – Majorna, Linnéstaden, Haga, Hisingen, Eriksberg, Högsbo, Frölunda and all other neighborhoods. We also cover surrounding municipalities such as Mölndal, Partille, Kungsbacka, Kungälv and Härryda.",
        },
        {
          question: "Is my furniture insured during assembly in Gothenburg?",
          answer:
            "Yes, all jobs are insured. If anything gets damaged during furniture assembly, we cover it. Our assemblers work carefully and handle your furniture with care.",
        },
        {
          question: "Do you remove packaging after assembly in Gothenburg?",
          answer:
            "Yes, we tidy up all cardboard, polystyrene and plastic wrap after assembly. We can take all packaging with us so you skip the recycling trip.",
        },
        {
          question: "Can you assemble kitchens in Gothenburg?",
          answer:
            "Yes, we assemble kitchen cabinets, countertops and kitchen islands across the Gothenburg area. We handle all carpentry – base cabinets, wall cabinets, doors, drawers and fittings. Electrical and plumbing connections need to be ordered separately.",
        },
        {
          question: "Do you assemble office furniture in Gothenburg?",
          answer:
            "Yes, we assemble office furniture across Gothenburg – sit-stand desks, office chairs, drawer units and meeting room furniture. We can work on evenings and weekends so assembly doesn't disrupt business operations.",
        },
        {
          question: "How long does it take to assemble a PAX wardrobe in Gothenburg?",
          answer:
            "A standard PAX wardrobe with two doors takes about 45–90 minutes. Larger systems with sliding doors, drawers and fittings can take 2–3 hours. We provide an estimated time when you book.",
        },
        {
          question: "Can you disassemble furniture in Gothenburg too?",
          answer:
            "Yes, we both assemble and disassemble furniture in Gothenburg. Perfect if you are moving and need to take apart wardrobes, beds or desks before the move.",
        },
        {
          question: "Do you assemble furniture on evenings and weekends in Gothenburg?",
          answer:
            "Yes, we offer furniture assembly in Gothenburg on weekdays, evenings and weekends. Pick the time that works best when booking.",
        },
        {
          question: "In which municipalities in the Gothenburg area do you offer furniture assembly?",
          answer:
            `We offer furniture assembly across the entire Gothenburg area: ${göteborgKommunerText}. We also cover all neighborhoods within Gothenburg city.`,
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.montering,

      pricingRows: getPackagePricingRows("montering", "en"),

      dynamicHeadings: {
        threeSteps: "Furniture assembly in Gothenburg in 3 easy steps",
        step2Title: "We assemble everything",
        step2Text:
          "Our assemblers in Gothenburg arrive by car with a complete toolbox. They unpack, assemble and check that everything fits perfectly. Packaging is tidied up.",
        step3Title: "Done",
        step3Desc:
          "Your furniture is assembled, adjusted and ready to use. Packaging has been cleared away and you can enjoy the result.",
        ctaHeading: "Book furniture assembly in Gothenburg today",
        ctaText:
          "Describe what furniture needs assembling, enter your Gothenburg address and see the price instantly. We bring all the tools – you don't need to lift a finger.",
      },
    },
  },

  {
    // Bud Göteborg — config för /bud-goteborg
    // Stadsspecifik sida för budtjänst i Göteborg.
    slug: "bud-goteborg",
    serviceCategory: "budtjänst",
    city: "göteborg",

    meta: {
      title:
        "Bud Göteborg – Boka Bud Samma Dag i Göteborg | Ungdomskraft",
      description:
        "Boka bud i Göteborg med leverans samma dag. Från kuvert till EU-pall – elmoped, bil eller skåpbil med bakgavellift. Expressbud inom 1 timme i centrala Göteborg.",
      h1: "Bud Göteborg",
    },

    hero: {
      heading: "Boka bud i Göteborg",
      intro:
        "Snabb budleverans i hela Göteborgsregionen – från Linnéstaden till Kungälv, Hisingen till Kungsbacka. Vi kör allt från kuvert till pallar med anpassat fordon och levererar ofta inom ett par timmar. Expressbud i centrala Göteborg inom 1 timme.",
      heroImage: "/hero-bud.png",
      heroImageAlt:
        "Ungdomskraft bud Göteborg – ungdomar lastar paket i skåpbil för snabb budleverans i Göteborg",
    },

    featureCards: {
      headingText: "Ditt bud i Göteborg",
      sinceText: "Budfirma i Göteborg sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft kört bud i Göteborgsregionen åt privatpersoner, företag och eventarrangörer. Vi känner stadsdelarna, Älvsborgsbron kontra Götaälvbron vid rusningstid och alla genvägar – det gör oss snabbare än rikstäckande budföretag.",
      bookYourText: "Boka ditt bud",
      bookDescText:
        "Beskriv vad du vill skicka, ange hämtadress och leveransadress i Göteborg. Du ser priset direkt och kan välja expressleverans.",
      vehicleTitleText: "Rätt fordon för Göteborgstrafiken",
      vehicleDescText:
        "Elmoped för brev och småpaket i centrum, liten bil för medelstora leveranser, skåpbil för större gods och stor skåpbil med bakgavellift för pallar. Vi väljer fordon efter trafikläge och godsstorlek.",
    },

    itemsSection: {
      heading: "Vanliga budleveranser i Göteborg",
      items: [
        {
          name: "Dokument & kontrakt",
          description:
            "Avtal, handlingar och juridiska dokument som ska mellan kontor, mäklare och advokatbyråer i Göteborg",
          image: "/box.webp",
        },
        {
          name: "E-handelspaket",
          description:
            "Returer, provkollektioner och expresspaket till kunder och butiker i Göteborgsområdet",
          image: "/box.webp",
        },
        {
          name: "Konst & skyltar",
          description:
            "Neonskyltar, tavlor, ljuslådor och banderoller till restauranger, gallerier och kontor i Göteborg",
          image: "/painting.webp",
        },
        {
          name: "Elektronik & teknik",
          description:
            "Servrar, skärmar och IT-utrustning som ska till kontor på Lindholmen, i Mölndal eller centrala Göteborg",
          image: "/electronics.webp",
        },
        {
          name: "Catering & eventleveranser",
          description:
            "Mat, dryck och utrustning till event, mässor och fester i hela Göteborgsregionen",
          image: "/appliances.webp",
        },
        {
          name: "Pallar & partileveranser",
          description:
            "EU-pallar med gods, dryckesleveranser och större volymer – bakgavellift vid behov",
          image: "/road-bike.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Bud i Göteborg – snabba leveranser i hela regionen",
      mainText:
        "Göteborg är en hamnstad med älven som delar staden i två halvor, broar som bildar flaskhalsar och ett vägnät som kräver lokal kunskap för att navigera effektivt. Det är precis därför Ungdomskraft finns i Göteborg. Våra budförare känner stadsdelarna utan och innan – från Avenyn och Haga till industriområdena på Hisingen, från de smala gatorna i Majorna till kontorshusen på Lindholmen. Vi vet att Götatunneln sparar tid vid rusningstid, att Oscarsleden korkar igen på eftermiddagen och var man parkerar utan att riskera böter i Linnéstaden. Det gör att vi levererar snabbare och smidigare än rikstäckande budföretag som skickar förare utan lokal Göteborgskännedom.\n\nVårt team av unga, energiska budförare kör allt från enstaka kuvert till fulla EU-pallar i hela Göteborgsregionen. Vi anpassar fordon efter uppdraget – elmoped tar sig snabbt genom Vasagatan och Östra Hamngatan, liten bil kör till Mölndal med ett par kartonger, skåpbil hanterar möbelleveranser till Torslanda och stor skåpbil med bakgavellift lossar pallar vid lastbryggor i Sisjöns handelsområde. Oavsett vad du behöver skicka har vi fordon och personal redo att köra inom Göteborgsområdet, ofta redan inom ett par timmar från bokning.",
      preparationHeading: "Förbereda ditt bud i Göteborg",
      preparationText:
        "Se till att godset är färdigpackat och redo att hämtas vid avtalad tid. Meddela oss om det finns portkod, trappor eller begränsad parkering vid hämtadressen – det är vanligt i centrala Göteborg, speciellt i Linnéstaden och Vasastaden. Om du sitter på ett kontor med reception, informera receptionen om att ett bud kommer. Ange alltid kontaktperson och telefonnummer vid både hämtning och leverans. Behöver du expressleverans i Göteborg? Boka tidigt på dagen så kan vi ofta leverera inom en timme i centrala stan.",
      additionalSections: [
        {
          heading: "Stadsdelar och kommuner vi kör bud i",
          content:
            `Vi kör bud i hela Göteborgsregionen. I centrala Göteborg – Inom Vallgraven, Haga, Linnéstaden, Vasastaden, Johanneberg – har vi flera budförare tillgängliga dagligen. Responstiden är kortast här, ofta under en timme vid expressbokning. På Hisingen – Lundby, Eriksberg, Backa, Torslanda – och i söder – Högsbo, Frölunda, Askim – har vi god täckning tack vare närheten till E6 och E20.\n\nVi täcker även kranskommunerna: ${göteborgKommunerText}. Leveranser mellan kommuner, till exempel från en leverantör i Kungälv till ett kontor i Mölndal, är vardagsmat för oss. Vi planerar alltid rutten för att minimera körtid och hålla nere kostnaden.`,
        },
        {
          heading: "Expressbud i centrala Göteborg",
          content:
            "Centrala Göteborg är vårt snabbaste leveransområde. Med elmoped tar vi oss genom Inom Vallgraven, Haga, Linnéstaden, Vasastaden och Johanneberg på minuter – utan att fastna i trafiken vid Järntorget eller leta parkering längs Linnégatan. Expressbud i centrala Göteborg levereras normalt inom 30–60 minuter. För tidskritiska dokument mellan kontor vid Lilla Bommen, provkollektioner till showrooms vid Avenyn eller akuta reservdelar till restauranger i Haga är expressmopeden det snabbaste alternativet. Även med bil och skåpbil erbjuder vi expressleverans i Göteborg – ditt uppdrag prioriteras framför allt annat och föraren kör raka vägen utan omvägar.",
        },
        {
          heading: "Bud till mässor och events i Göteborg",
          content:
            "Göteborg är en av Sveriges främsta eventstäder med Svenska Mässan, Scandinavium, Ullevi, Liseberg och Eriksbergshallen som några av de mest anlitade arenorna. Vi kör eventleveranser dagligen i Göteborg och vet vad som krävs: tajt tidshållning, varsam hantering av ömtåligt material och flexibilitet att anpassa oss efter sena ändringar. Oavsett om det handlar om att leverera neonskyltar till en restaurangöppning på Tredje Långgatan, utrustning till en mässa på Svenska Mässan eller catering till en företagskonferens på Lindholmen – vi löser det. Med bakgavellift hanterar vi pallar med dryck, scendekor och annat tungt eventgods direkt vid lastbrygga.",
        },
        {
          heading: "Varför boka bud i Göteborg med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal Göteborgskunskap –",
              text: "Vi känner trafikmönster, broar, genvägar och parkeringsmöjligheter i hela Göteborgsregionen. Det gör leveranserna snabbare och billigare.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har budförare redo i Göteborg dagligen, inklusive kvällar och helger. Expressbud i centrala Göteborg ofta inom 30–60 minuter.",
            },
            {
              label: "Anpassat fordon –",
              text: "Elmoped i centrum, bil för mellanstora leveranser, skåpbil med bakgavellift för pallar. Alltid rätt fordon för Göteborgstrafiken.",
            },
            {
              label: "Fast pris direkt –",
              text: "Du ser priset vid bokning. Inga dolda avgifter, inga tilläggsavgifter för centrala Göteborg.",
            },
            {
              label: "Stödjer unga –",
              text: "Varje bud du bokar ger en ung Göteborgare möjlighet att jobba och växa. Budfirma med socialt engagemang sedan 2014.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Hur snabbt kan ni leverera bud i Göteborg?",
        answer:
          "Med expressleverans kan vi ofta leverera inom 30–60 minuter i centrala Göteborg. Standardleverans samma dag innebär leverans inom 2–4 timmar beroende på avstånd inom Göteborgsregionen.",
      },
      {
        question: "Vad kostar det att skicka bud i Göteborg?",
        answer:
          "Priset beror på fordon, avstånd och tid. Extra Liten (elmoped) från 595 kr, Small (liten bil) från 995 kr, Medium (skåpbil) från 1 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Vilka stadsdelar i Göteborg kör ni bud i?",
        answer:
          `Vi kör bud i hela Göteborgsregionen – centrum, Hisingen, söderort och alla kranskommuner: ${göteborgKommunerText}. Ingen del av Göteborg är utanför vårt leveransområde.`,
      },
      {
        question: "Kan ni köra expressbud i centrala Göteborg?",
        answer:
          "Ja, expressbud i centrala Göteborg är vår snabbaste tjänst. Med elmoped levererar vi dokument, nycklar och småpaket inom 30–60 minuter i Inom Vallgraven, Haga, Linnéstaden och Vasastaden.",
      },
      {
        question: "Vilka fordon har ni i Göteborg?",
        answer:
          "Vi har el-driven moped för brev och småpaket i centrum, liten bil för medelstora leveranser, skåpbil för större gods och stor skåpbil med bakgavellift för pallar och tungt gods.",
      },
      {
        question: "Kan ni leverera till mässor och events i Göteborg?",
        answer:
          "Ja, vi kör dagligen eventleveranser i Göteborg – catering, utrustning, skyltar och dryck till Svenska Mässan, Scandinavium, Eriksbergshallen och andra eventlokaler.",
      },
      {
        question: "Kör ni bud till kranskommuner som Mölndal och Kungälv?",
        answer:
          "Ja, vi kör bud i hela Göteborgsregionen inklusive alla kranskommuner. Leveranser mellan kommuner, till exempel från Kungälv till Mölndal, är vardagsmat för oss.",
      },
      {
        question: "Kan jag boka bud i Göteborg samma dag?",
        answer:
          "Ja, de flesta buduppdrag i Göteborg hämtas och levereras samma dag. Vi bekräftar upphämtning och leveranstid direkt vid bokning.",
      },
      {
        question: "Erbjuder ni företagsavtal för bud i Göteborg?",
        answer:
          "Ja, vi erbjuder avtalspriser för Göteborgsbaserade företag med återkommande leveransbehov. Rabatterade priser, fasta hämtningstider och samlad fakturering.",
      },
      {
        question: "Kan ni köra en EU-pall i Göteborg?",
        answer:
          "Ja, vi har stor skåpbil med bakgavellift som hanterar EU-pallar upp till 800 kg. Vi lastar och lossar med liften – ingen truck behövs på mottagande sida.",
      },
      {
        question: "Hanterar ni ömtåliga föremål i Göteborg?",
        answer:
          "Ja, vi kör neonskyltar, konst, elektronik, tårtor och andra ömtåliga föremål i Göteborg dagligen. Vi rekommenderar ordentlig förpackning, men våra förare hanterar allt varsamt.",
      },
      {
        question: "Kör ni bud på kvällar och helger i Göteborg?",
        answer:
          "Ja, vi kör bud i Göteborg även på kvällar och helger. Tillgängligheten kan variera, men du ser lediga tider direkt vid bokning.",
      },
      {
        question: "Hur bokar jag bud i Göteborg?",
        answer:
          "Du bokar direkt på vår hemsida. Beskriv vad du vill skicka, ange hämtadress och leveransadress i Göteborg, välj tid och se priset direkt.",
      },
      {
        question: "Är mina saker försäkrade under budtransporten i Göteborg?",
        answer:
          "Ja, alla budleveranser i Göteborg är försäkrade under transporten. Vid ömtåliga eller värdefulla föremål rekommenderar vi att du meddelar oss i förväg.",
      },
      {
        question: "Vad är skillnaden mellan standardbud och expressbud i Göteborg?",
        answer:
          "Standardbud levereras inom 2–4 timmar. Expressbud prioriteras direkt och levereras så snabbt som möjligt – ofta inom 30–60 minuter i centrala Göteborg. Expressbud kostar lite mer men sparar tid.",
      },
      {
        question: "Kan budet göra flera stopp i Göteborg?",
        answer:
          "Ja, du kan lägga till flera hämtnings- och leveransadresser i samma bokning. Perfekt om du behöver hämta från en leverantör på Hisingen och leverera till två kontor i centrum.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Bud i Göteborg i 3 steg",
      step2Text:
        "Vår budförare hämtar ditt gods i Göteborg och kör direkt till mottagaren. Du får en bekräftelse när leveransen är genomförd.",
      step3Title: "Levererat",
      step3Desc:
        "Ditt gods har anlänt till rätt adress i Göteborg. Mottagaren bekräftar leveransen och uppdraget är klart.",
      ctaHeading: "Skicka bud i Göteborg idag",
      ctaText:
        "Beskriv vad du vill skicka, ange hämtadress och leveransadress i Göteborg. Du ser priset direkt och kan välja expressleverans.",
    },

    en: {
      slug: "courier-gothenburg",
      meta: {
        title:
          "Courier Gothenburg – Same Day Delivery in Gothenburg | Ungdomskraft",
        description:
          "Book a courier in Gothenburg with same-day delivery. From envelopes to EU pallets – electric moped, car or van with tail lift. Express delivery within 1 hour in central Gothenburg.",
        h1: "Courier Gothenburg",
      },

      hero: {
        heading: "Book a courier in Gothenburg",
        intro:
          "Fast courier delivery across the Gothenburg region – from Linnéstaden to Kungälv, Hisingen to Kungsbacka. We deliver everything from envelopes to pallets with the right vehicle and often within a couple of hours. Express delivery in central Gothenburg within 1 hour.",
        heroImageAlt:
          "Ungdomskraft courier Gothenburg – young workers loading packages into delivery van in Gothenburg",
      },

      featureCards: {
        headingText: "Your Gothenburg courier",
        sinceText: "Courier service in Gothenburg since 2014",
        storyText:
          "Since 2014, Ungdomskraft has been running courier deliveries across the Gothenburg region for individuals, businesses and event organizers. We know the neighborhoods, bridge bottlenecks and shortcuts – making us faster than the big national courier companies.",
        bookYourText: "Book your courier",
        bookDescText:
          "Describe what you want to send, enter pickup and delivery addresses in Gothenburg. You see the price instantly and can choose express delivery.",
        vehicleTitleText: "The right vehicle for Gothenburg traffic",
        vehicleDescText:
          "Electric moped for letters and small packages in the city center, small car for medium deliveries, van for larger goods, and large van with tail lift for pallets. We choose the vehicle based on traffic and cargo size.",
      },

      itemsSection: {
        heading: "Common courier deliveries in Gothenburg",
        items: [
          {
            name: "Documents & contracts",
            description:
              "Agreements, legal documents and paperwork between offices, real estate agents and law firms in Gothenburg",
            image: "/box.webp",
          },
          {
            name: "E-commerce packages",
            description:
              "Returns, sample collections and express packages to customers and stores in the Gothenburg area",
            image: "/box.webp",
          },
          {
            name: "Art & signage",
            description:
              "Neon signs, paintings, light boxes and banners to restaurants, galleries and offices in Gothenburg",
            image: "/painting.webp",
          },
          {
            name: "Electronics & tech",
            description:
              "Servers, monitors and IT equipment to offices at Lindholmen, in Mölndal or central Gothenburg",
            image: "/electronics.webp",
          },
          {
            name: "Catering & event deliveries",
            description:
              "Food, beverages and equipment for events, trade fairs and parties across the Gothenburg region",
            image: "/appliances.webp",
          },
          {
            name: "Pallets & bulk deliveries",
            description:
              "EU pallets, beverage deliveries and larger volumes – tail lift available when needed",
            image: "/road-bike.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "Courier in Gothenburg – fast deliveries across the region",
        mainText:
          "Gothenburg is a port city where the river divides the city in two, bridges create bottlenecks and the road network demands local knowledge to navigate efficiently. That is exactly why Ungdomskraft operates in Gothenburg. Our couriers know the city's neighborhoods inside and out – from Avenyn and Haga to the industrial areas on Hisingen, from the narrow streets of Majorna to the office buildings at Lindholmen. We know that the Göta Tunnel saves time at rush hour, that Oscarsleden gets congested in the afternoon and where to park without risking fines in Linnéstaden. This means we deliver faster and more smoothly than nationwide courier companies that send drivers without local Gothenburg knowledge.\n\nOur team of young, energetic couriers delivers everything from single envelopes to full EU pallets across the Gothenburg region. We match the vehicle to the job – electric moped moves quickly through Vasagatan and Östra Hamngatan, a small car drives to Mölndal with a couple of boxes, a van handles furniture deliveries to Torslanda, and a large van with tail lift unloads pallets at loading docks in Sisjön's retail area. Whatever you need to send, we have vehicles and staff ready to go within the Gothenburg area, often within just a couple of hours from booking.",
        preparationHeading: "Preparing your Gothenburg courier job",
        preparationText:
          "Make sure your goods are packed and ready for pickup at the agreed time. Let us know about entry codes, stairs or limited parking at the pickup address – this is common in central Gothenburg, especially in Linnéstaden and Vasastaden. If you are in an office with a reception desk, let them know a courier is coming. Always provide a contact person and phone number at both pickup and delivery. Need express delivery in Gothenburg? Book early in the day and we can often deliver within one hour in the city center.",
        additionalSections: [
          {
            heading: "Neighborhoods and municipalities we cover",
            content:
              `We deliver across the entire Gothenburg region. In central Gothenburg – Inom Vallgraven, Haga, Linnéstaden, Vasastaden, Johanneberg – we have multiple couriers available daily. Response times are shortest here, often under one hour with express booking. On Hisingen – Lundby, Eriksberg, Backa, Torslanda – and in the south – Högsbo, Frölunda, Askim – we have strong coverage thanks to proximity to the E6 and E20 motorways.\n\nWe also cover all surrounding municipalities: ${göteborgKommunerText}. Deliveries between municipalities, for example from a supplier in Kungälv to an office in Mölndal, are everyday business for us. We always plan the route to minimize driving time and keep costs down for you.`,
          },
          {
            heading: "Express courier in central Gothenburg",
            content:
              "Central Gothenburg is our fastest delivery zone. By electric moped, we move through Inom Vallgraven, Haga, Linnéstaden, Vasastaden and Johanneberg in minutes – without getting stuck in traffic at Järntorget or searching for parking on Linnégatan. Express deliveries in central Gothenburg are typically completed within 30–60 minutes. For time-critical documents between offices at Lilla Bommen, sample collections to showrooms on Avenyn or urgent spare parts to restaurants in Haga, the express moped is the fastest option. We also offer express delivery by car and van in Gothenburg – your job gets top priority and the driver takes the most direct route.",
          },
          {
            heading: "Event and trade fair deliveries in Gothenburg",
            content:
              "Gothenburg is one of Sweden's leading event cities, with Svenska Mässan, Scandinavium, Ullevi, Liseberg and Eriksbergshallen among the most popular venues. We run event deliveries daily in Gothenburg and know what it takes: tight scheduling, careful handling of fragile materials and flexibility to adapt to last-minute changes. Whether it is neon signs for a restaurant opening on Tredje Långgatan, equipment for a trade fair at Svenska Mässan or catering for a corporate conference at Lindholmen – we handle it. With our tail lift, we manage pallets of beverages, stage decor and other heavy event goods directly at loading docks.",
          },
          {
            heading: "Why book a courier in Gothenburg with Ungdomskraft?",
            listItems: [
              {
                label: "Local Gothenburg expertise –",
                text: "We know traffic patterns, bridges, shortcuts and parking options across the Gothenburg region. This makes deliveries faster and more affordable.",
              },
              {
                label: "Fast availability –",
                text: "We have couriers ready in Gothenburg daily, including evenings and weekends. Express delivery in central Gothenburg often within 30–60 minutes.",
              },
              {
                label: "Right vehicle –",
                text: "Electric moped in the city, car for medium deliveries, van with tail lift for pallets. Always the right vehicle for Gothenburg traffic.",
              },
              {
                label: "Fixed price upfront –",
                text: "You see the price when booking. No hidden fees, no surcharges for central Gothenburg.",
              },
              {
                label: "Supporting young people –",
                text: "Every courier you book gives a young Gothenburger the opportunity to work and grow. Courier service with social impact since 2014.",
              },
            ],
          },
        ],
      },

      faq: [
        {
          question: "How fast can you deliver a courier in Gothenburg?",
          answer:
            "With express delivery, we can often deliver within 30–60 minutes in central Gothenburg. Standard same-day delivery means delivery within 2–4 hours depending on distance within the Gothenburg region.",
        },
        {
          question: "How much does courier service in Gothenburg cost?",
          answer:
            "The price depends on vehicle, distance and time. Extra Liten (electric moped) from SEK 595, Small (small car) from SEK 995, Medium (van) from SEK 1,495. You see the price instantly when booking.",
        },
        {
          question: "Which Gothenburg neighborhoods do you cover?",
          answer:
            `We deliver across the entire Gothenburg region – the city center, Hisingen, southern suburbs and all surrounding municipalities: ${göteborgKommunerText}. No part of Gothenburg is outside our delivery area.`,
        },
        {
          question: "Can you do express delivery in central Gothenburg?",
          answer:
            "Yes, express delivery in central Gothenburg is our fastest service. By electric moped, we deliver documents, keys and small packages within 30–60 minutes in Inom Vallgraven, Haga, Linnéstaden and Vasastaden.",
        },
        {
          question: "What vehicles do you have in Gothenburg?",
          answer:
            "We have electric mopeds for letters and small packages in the city center, small cars for medium deliveries, vans for larger goods, and large vans with tail lift for pallets and heavy cargo.",
        },
        {
          question: "Can you deliver to trade fairs and events in Gothenburg?",
          answer:
            "Yes, we run daily event deliveries in Gothenburg – catering, equipment, signage and beverages to Svenska Mässan, Scandinavium, Eriksbergshallen and other event venues.",
        },
        {
          question: "Do you deliver to suburbs like Mölndal and Kungälv?",
          answer:
            "Yes, we deliver across the entire Gothenburg region including all surrounding municipalities. Deliveries between municipalities, for example from Kungälv to Mölndal, are everyday business for us.",
        },
        {
          question: "Can I book a same-day courier in Gothenburg?",
          answer:
            "Yes, most courier jobs in Gothenburg are picked up and delivered the same day. We confirm pickup and delivery time immediately when you book.",
        },
        {
          question: "Do you offer business contracts in Gothenburg?",
          answer:
            "Yes, we offer contract pricing for Gothenburg-based businesses with recurring delivery needs. Discounted rates, scheduled pickups and consolidated invoicing.",
        },
        {
          question: "Can you deliver a EU pallet in Gothenburg?",
          answer:
            "Yes, we have a large van with tail lift that handles EU pallets up to 800 kg. We load and unload with the lift – no forklift needed at the receiving end.",
        },
        {
          question: "Do you handle fragile items in Gothenburg?",
          answer:
            "Yes, we deliver neon signs, artwork, electronics, cakes and other fragile items in Gothenburg daily. We recommend proper packaging, but our couriers handle everything with care.",
        },
        {
          question: "Do you operate evenings and weekends in Gothenburg?",
          answer:
            "Yes, we offer courier service in Gothenburg on evenings and weekends. Availability may vary, but you can see available times when booking.",
        },
        {
          question: "How do I book a courier in Gothenburg?",
          answer:
            "You book directly on our website. Describe what you want to send, enter pickup and delivery addresses in Gothenburg, choose a time and see the price instantly.",
        },
        {
          question: "Are my goods insured during the delivery?",
          answer:
            "Yes, all courier deliveries in Gothenburg are insured during transport. For fragile or valuable items, we recommend letting us know in advance.",
        },
        {
          question: "What is the difference between standard and express courier in Gothenburg?",
          answer:
            "Standard courier delivers within 2–4 hours. Express courier is prioritized immediately and delivered as fast as possible – often within 30–60 minutes in central Gothenburg. Express costs a bit more but saves time.",
        },
        {
          question: "Can the courier make multiple stops in Gothenburg?",
          answer:
            "Yes, you can add multiple pickup and delivery addresses in the same booking. Perfect if you need to pick up from a supplier on Hisingen and deliver to two offices in the city center.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bud,

      pricingRows: getPackagePricingRows("bud", "en"),

      dynamicHeadings: {
        threeSteps: "Courier in Gothenburg in 3 steps",
        step2Text:
          "Our courier picks up your goods in Gothenburg and drives directly to the recipient. You receive confirmation when the delivery is complete.",
        step3Title: "Delivered",
        step3Desc:
          "Your goods have arrived at the right address in Gothenburg. The recipient confirms delivery and the job is done.",
        ctaHeading: "Book a courier in Gothenburg today",
        ctaText:
          "Describe what you want to send, enter pickup and delivery addresses in Gothenburg. You see the price instantly and can choose express delivery.",
      },
    },
  },

  {
    // Budbil Göteborg — config för /budbil-goteborg
    // Stadsspecifik sida för budbil med chaufför i Göteborg.
    slug: "budbil-goteborg",
    serviceCategory: "budtjänst",
    city: "göteborg",

    meta: {
      title:
        "Budbil Göteborg – Boka Budbil med Chaufför Samma Dag | Ungdomskraft",
      description:
        "Boka budbil i Göteborg med chaufför och bärhjälp. Skåpbil med bakgavellift vid behov. Leverans samma dag i hela Göteborgsregionen. Budbil sedan 2014.",
      h1: "Budbil Göteborg",
    },

    hero: {
      heading: "Boka budbil i Göteborg",
      intro:
        "Behöver du en budbil med chaufför i Göteborg som hämtar och levererar samma dag? Vi kör allt från kartonger till fullastade pallar i hela Göteborgsregionen. Du får rätt fordon, starka armar och snabb leverans – utan att själv lyfta ett finger.",
      heroImage: "/hero-bud.png",
      heroImageAlt:
        "Ungdomskraft budbil Göteborg – ungdomar lastar paket i skåpbil för snabb leverans i Göteborg",
    },

    featureCards: {
      headingText: "Din budbil i Göteborg",
      sinceText: "Budbil i Göteborg sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft kört budbil i Göteborgsregionen åt privatpersoner, företag och eventarrangörer. Våra unga förare känner varje stadsdel, vet vilken bro som är snabbast vid rusningstid och navigerar Göteborgstrafiken bättre än de stora budföretagen.",
      bookYourText: "Boka din budbil",
      bookDescText:
        "Beskriv vad som ska hämtas, ange adresser i Göteborg och se priset direkt. Vi matchar rätt budbil efter ditt gods.",
      vehicleTitleText: "Rätt budbil för Göteborgstrafiken",
      vehicleDescText:
        "Liten bil för snabba leveranser i centrum, skåpbil för större volymer till Torslanda eller Mölndal, stor skåpbil med bakgavellift för pallar till Sisjön. Du betalar bara för den kapacitet du behöver.",
    },

    itemsSection: {
      heading: "Det här kör vi med budbil i Göteborg",
      items: [
        {
          name: "Kontorsflytt & inventarier",
          description:
            "Kontorsstolar, skrivbord, skärmar och arkivskåp mellan kontor på Lindholmen, i Mölndal och centrala Göteborg",
          image: "/furniture.webp",
        },
        {
          name: "Blocket- & Marketplace-köp",
          description:
            "Möbler, elektronik och prylar du köpt av privatpersoner i Göteborg – vi hämtar och kör hem till dig",
          image: "/box.webp",
        },
        {
          name: "Restaurangleveranser",
          description:
            "Storsändningar av livsmedel, dryck och utrustning till restauranger i Haga, på Avenyn och Tredje Långgatan",
          image: "/appliances.webp",
        },
        {
          name: "Mäss- & eventleveranser",
          description:
            "Scendekor, ljud, belysning och catering till event på Svenska Mässan, Scandinavium och Eriksbergshallen",
          image: "/painting.webp",
        },
        {
          name: "E-handel & returer",
          description:
            "Returpaket, provkollektioner och expressleveranser till e-handlare och butiker i Göteborgsområdet",
          image: "/box.webp",
        },
        {
          name: "Pallar & tungt gods",
          description:
            "EU-pallar med varor, maskindelar och tungt gods – budbil med bakgavellift till lastbryggor i hela regionen",
          image: "/road-bike.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Budbil i Göteborg – fordon, chaufför och bärhjälp i hela regionen",
      mainText:
        "Att köra budbil i Göteborg kräver mer än bara ett fordon. Det kräver lokalkännedom – vetskap om att Götatunneln sparar tid vid rusningstid, att Älvsborgsbron ofta är ett snabbare alternativ till Tingstadstunneln på eftermiddagen, och att parkering i Linnéstaden är en utmaning oavsett tid på dygnet. Ungdomskrafts budbilsförare har den kunskapen. De kör i Göteborgsregionen dagligen och vet exakt hur man tar sig fram snabbast.\n\nMed Ungdomskraft får du mer än en budbil – du får ett komplett team. Våra unga, starka förare lastar, bär uppför trappor i landshövdingehus i Majorna, navigerar trånga trapphus i jugendbyggnader i Vasastaden och levererar hela vägen in genom dörren. Du behöver varken hyra bil, be kompisar om hjälp eller ta ledigt från jobbet. Beskriv vad du behöver transportera så matchar vi rätt budbil: liten bil för snabba leveranser mellan kontor i centrum, skåpbil för möbler och kartonger till Torslanda eller Partille, och stor skåpbil med bakgavellift för pallar och tungt gods till industriområden i Sisjön eller Arendal. Du ser priset direkt innan du bokar – inga dolda avgifter.",
      preparationHeading: "Förbered din budbil-leverans i Göteborg",
      preparationText:
        "Se till att godset är färdigpackat och tillgängligt vid hämtning. I centrala Göteborg är parkering ofta begränsad – meddela oss om det finns innergård, garageinfart eller lastzon vid hämtadressen, särskilt i Linnéstaden, Haga och Vasastaden. Det sparar tid och håller nere kostnaden. Möbler som ska isärtagas bör vara demonterade i förväg. Om du bor i lägenhet, kontrollera om hissen tar det som ska transporteras – våra förare bär givetvis i trappor om det behövs. Ange portkod, våning och kontaktperson vid båda adresserna.",
      additionalSections: [
        {
          heading: "Stadsdelar och kommuner vi kör budbil i",
          content:
            `Vi kör budbil i hela Göteborgsregionen. I centrala Göteborg – Inom Vallgraven, Haga, Linnéstaden, Vasastaden, Johanneberg – har vi budbilsförare tillgängliga dagligen med kort responstid. På Hisingen – Lundby, Eriksberg, Backa, Torslanda – och i söder – Högsbo, Frölunda, Askim – når vi er snabbt via E6 och E20.\n\nVi täcker även alla kranskommuner: ${göteborgKommunerText}. Leveranser tvärs över regionen, till exempel en kontorsflytt från Lindholmen till Mölndal eller en palleverans från Kungälv till Kungsbacka, är vardagsmat. Vi planerar alltid den snabbaste rutten.`,
        },
        {
          heading: "Budbil samma dag i Göteborg",
          content:
            "De flesta som söker budbil i Göteborg behöver leveransen idag, inte imorgon. Därför erbjuder vi budbil med leverans samma dag som standard i hela Göteborgsregionen. Du bokar, vi bekräftar och vår chaufför är på plats inom ett par timmar. Behöver du det snabbare? Välj expressleverans – ditt uppdrag prioriteras direkt och budbilen åker raka vägen utan omvägar. I centrala Göteborg kan expressleverans med liten bil innebära 30–60 minuter. Med skåpbil räkna med en till två timmar beroende på avstånd och om rutten går via Hisingen eller söderort.",
        },
        {
          heading: "Budbil för Göteborgsföretag – avtal och fasta flöden",
          content:
            "Driver du ett företag i Göteborg med regelbundna leveransbehov? Vi erbjuder företagsavtal med rabatterade budbilspriser, schemalagda hämtningar och dedikerade förare som lär känna ditt företag. Techbolag på Lindholmen som skickar utrustning dagligen, restauranger i Haga med leverantörsleveranser, e-handlare i Mölndal med returer och arkitektkontor vid Lilla Bommen med materialprover – alla får ett skräddarsytt upplägg. Med ett företagsavtal slipper du boka varje enskilt uppdrag. Budbilen kommer enligt schema, och du får en samlad faktura varje månad.",
        },
        {
          heading: "Varför boka budbil i Göteborg med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal Göteborgskunskap –",
              text: "Våra förare kör i Göteborg dagligen och känner trafikmönster, broar, parkeringsmöjligheter och genvägar i varje stadsdel. Det ger snabbare leveranser och lägre kostnad.",
            },
            {
              label: "Bärhjälp ingår alltid –",
              text: "Våra unga förare lastar, bär uppför trappor och levererar hela vägen in. Du behöver inte lyfta ett finger – oavsett om det är ett landshövdingehus i Majorna eller ett kontor på Lindholmen.",
            },
            {
              label: "Rätt budbil för uppdraget –",
              text: "Liten bil för snabba stadsärenden, skåpbil för möbler, stor skåpbil med bakgavellift för pallar. Alltid rätt fordon för Göteborgstrafiken.",
            },
            {
              label: "Fast pris direkt –",
              text: "Du ser priset vid bokning. Inga dolda avgifter, inga tilläggsavgifter för centrala Göteborg eller kranskommuner.",
            },
            {
              label: "Stödjer unga Göteborgare –",
              text: "Varje budbil du bokar ger en ung person chansen att jobba och växa. Budbil med socialt engagemang sedan 2014.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar det att boka budbil i Göteborg?",
        answer:
          "Priset beror på fordon, avstånd och tid. Liten bil från 995 kr, skåpbil från 1 495 kr, stor skåpbil med bakgavellift från 2 495 kr. Du ser priset direkt vid bokning.",
      },
      {
        question: "Hur snabbt kan budbilen vara på plats i Göteborg?",
        answer:
          "Med standardleverans är budbilen på plats inom 2–4 timmar. Med expressleverans prioriteras ditt uppdrag direkt – ofta inom 30–60 minuter i centrala Göteborg.",
      },
      {
        question: "Vilka stadsdelar i Göteborg kör ni budbil i?",
        answer:
          `Vi kör budbil i hela Göteborgsregionen – centrum, Hisingen, söderort och alla kranskommuner: ${göteborgKommunerText}. Ingen del av Göteborg är utanför vårt leveransområde.`,
      },
      {
        question: "Ingår bärhjälp med budbilen i Göteborg?",
        answer:
          "Ja, bärhjälp ingår alltid. Våra förare lastar, bär uppför trappor och levererar hela vägen in – oavsett om det är ett landshövdingehus i Majorna eller ett kontorshus i Mölndal.",
      },
      {
        question: "Kan budbilen ta en EU-pall i Göteborg?",
        answer:
          "Ja, vår stora budbil har bakgavellift och hanterar EU-pallar upp till 800 kg. Vi lastar och lossar med liften – ingen truck behövs.",
      },
      {
        question: "Kan jag boka budbil i Göteborg samma dag?",
        answer:
          "Ja, de flesta budbil-uppdrag i Göteborg hämtas och levereras samma dag. Vi bekräftar tid direkt vid bokning.",
      },
      {
        question: "Vilka fordon har ni som budbil i Göteborg?",
        answer:
          "Liten bil för snabba leveranser i centrum, skåpbil för större volymer och möbler, stor skåpbil med bakgavellift för pallar och tungt gods.",
      },
      {
        question: "Kör budbilen till kranskommuner som Mölndal och Kungälv?",
        answer:
          "Ja, vi kör budbil i hela Göteborgsregionen inklusive alla kranskommuner. Leveranser från exempelvis Kungälv till Mölndal är vardag för oss.",
      },
      {
        question: "Kan budbilen hämta Blocket-köp i Göteborg?",
        answer:
          "Ja, vi hämtar möbler, elektronik och annat du köpt av privatpersoner i Göteborg. Beskriv vad som ska hämtas, ange adresser och vi kör det hem till dig samma dag.",
      },
      {
        question: "Erbjuder ni företagsavtal för budbil i Göteborg?",
        answer:
          "Ja, vi erbjuder avtalspriser för Göteborgsföretag med återkommande behov av budbil. Rabatterade priser, fasta hämtningstider och samlad fakturering.",
      },
      {
        question: "Hanterar budbilen ömtåliga föremål i Göteborg?",
        answer:
          "Ja, vi transporterar neonskyltar, konst, elektronik och andra ömtåliga saker i Göteborg dagligen. Vi rekommenderar ordentlig förpackning, men våra förare hanterar allt varsamt.",
      },
      {
        question: "Kör budbilen på kvällar och helger i Göteborg?",
        answer:
          "Ja, vi erbjuder budbil i Göteborg även på kvällar och helger. Tillgängligheten kan variera – du ser lediga tider direkt vid bokning.",
      },
      {
        question: "Hur bokar jag budbil i Göteborg?",
        answer:
          "Du bokar direkt på vår hemsida. Beskriv vad du vill transportera, ange hämtadress och leveransadress i Göteborg, och se priset direkt.",
      },
      {
        question: "Är mina saker försäkrade i budbilen?",
        answer:
          "Ja, alla transporter med budbil i Göteborg är försäkrade. Vid ömtåliga eller värdefulla föremål rekommenderar vi att du meddelar oss i förväg.",
      },
      {
        question: "Vad är skillnaden mellan budbil och vanligt bud i Göteborg?",
        answer:
          "Ett vanligt bud kan skickas med elmoped eller liten bil för lätta leveranser. En budbil innebär ett större fordon – skåpbil eller skåpbil med bakgavellift – med chaufför och bärhjälp för tyngre gods.",
      },
      {
        question: "Kan budbilen göra flera stopp i Göteborg?",
        answer:
          "Ja, du kan lägga till flera hämtnings- och leveransadresser i samma bokning. Perfekt om du behöver hämta från en leverantör på Hisingen och leverera till kontor i centrum och Mölndal.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Budbil i Göteborg i 3 steg",
      step2Text:
        "Vår chaufför hämtar ditt gods i Göteborg, lastar budbilen och kör direkt till mottagaren. Du får en bekräftelse när leveransen är genomförd.",
      step3Title: "Levererat",
      step3Desc:
        "Ditt gods har anlänt. Chauffören har burit in och ställt på plats i Göteborg – uppdraget är klart.",
      ctaHeading: "Boka budbil i Göteborg idag",
      ctaText:
        "Beskriv vad du vill transportera, ange hämtadress och leveransadress i Göteborg. Du ser priset direkt och kan välja expressleverans.",
    },

    en: {
      slug: "delivery-van-gothenburg",
      meta: {
        title:
          "Delivery Van Gothenburg – Book a Van with Driver Same Day | Ungdomskraft",
        description:
          "Book a delivery van with driver in Gothenburg. Van with tail lift available. Same-day delivery across the Gothenburg region with carrying help included.",
        h1: "Delivery Van Gothenburg",
      },

      hero: {
        heading: "Book a delivery van in Gothenburg",
        intro:
          "Need a delivery van with a driver in Gothenburg who picks up and delivers the same day? We handle everything from boxes to fully loaded pallets across the Gothenburg region. You get the right vehicle, strong arms and fast delivery – without lifting a finger.",
        heroImageAlt:
          "Ungdomskraft delivery van Gothenburg – young workers loading packages for same-day delivery in Gothenburg",
      },

      featureCards: {
        headingText: "Your delivery van in Gothenburg",
        sinceText: "Delivery van in Gothenburg since 2014",
        storyText:
          "Since 2014, Ungdomskraft has been running delivery vans across the Gothenburg region for individuals, businesses and event organizers. Our young drivers know every neighborhood, which bridge is fastest at rush hour, and navigate Gothenburg traffic better than the big courier companies.",
        bookYourText: "Book your delivery van",
        bookDescText:
          "Describe what needs to be picked up, enter addresses in Gothenburg and see the price instantly. We match the right van to your cargo.",
        vehicleTitleText: "The right van for Gothenburg traffic",
        vehicleDescText:
          "Small car for quick deliveries downtown, van for larger volumes to Torslanda or Mölndal, large van with tail lift for pallets to Sisjön. You only pay for the capacity you need.",
      },

      itemsSection: {
        heading: "What we deliver by van in Gothenburg",
        items: [
          {
            name: "Office moves & inventory",
            description:
              "Office chairs, desks, monitors and filing cabinets between offices at Lindholmen, in Mölndal and central Gothenburg",
            image: "/furniture.webp",
          },
          {
            name: "Blocket & Marketplace purchases",
            description:
              "Furniture, electronics and items you bought from private sellers in Gothenburg – we pick up and deliver to your door",
            image: "/box.webp",
          },
          {
            name: "Restaurant deliveries",
            description:
              "Bulk food, beverages and equipment to restaurants in Haga, on Avenyn and Tredje Långgatan",
            image: "/appliances.webp",
          },
          {
            name: "Trade fair & event deliveries",
            description:
              "Stage decor, sound, lighting and catering for events at Svenska Mässan, Scandinavium and Eriksbergshallen",
            image: "/painting.webp",
          },
          {
            name: "E-commerce & returns",
            description:
              "Return packages, sample collections and express deliveries to e-commerce businesses and stores in the Gothenburg area",
            image: "/box.webp",
          },
          {
            name: "Pallets & heavy goods",
            description:
              "EU pallets, machine parts and heavy cargo – delivery van with tail lift to loading docks across the region",
            image: "/road-bike.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "Delivery van in Gothenburg – vehicle, driver and carrying help across the region",
        mainText:
          "Driving a delivery van in Gothenburg requires more than just a vehicle. It requires local knowledge – knowing that the Göta Tunnel saves time at rush hour, that Älvsborgsbron is often a faster alternative to the Tingstad Tunnel in the afternoon, and that parking in Linnéstaden is a challenge regardless of the time of day. Ungdomskraft's delivery van drivers have that knowledge. They drive across the Gothenburg region daily and know exactly how to get there fastest.\n\nWith Ungdomskraft, you get more than a delivery van – you get a complete team. Our young, strong drivers load, carry up stairs in traditional landshövdingehus in Majorna, navigate narrow stairwells in Art Nouveau buildings in Vasastaden, and deliver all the way through the door. You don't need to rent a van, ask friends for help or take time off work. Describe what you need transported and we match the right van: small car for quick deliveries between offices downtown, van for furniture and boxes to Torslanda or Partille, and large van with tail lift for pallets and heavy goods to industrial areas in Sisjön or Arendal. You see the price upfront before you book – no hidden fees.",
        preparationHeading: "Prepare your delivery van pickup in Gothenburg",
        preparationText:
          "Make sure goods are packed and accessible at pickup. In central Gothenburg, parking is often limited – let us know if there is a courtyard, garage entrance or loading zone at the pickup address, especially in Linnéstaden, Haga and Vasastaden. This saves time and keeps costs down. Furniture that needs disassembly should be taken apart in advance. If you live in an apartment, check whether the elevator fits what needs to be transported – our drivers will carry up stairs if needed. Provide entry code, floor number and contact person at both addresses.",
        additionalSections: [
          {
            heading: "Neighborhoods and municipalities we cover",
            content:
              `We operate delivery vans across the entire Gothenburg region. In central Gothenburg – Inom Vallgraven, Haga, Linnéstaden, Vasastaden, Johanneberg – we have van drivers available daily with short response times. On Hisingen – Lundby, Eriksberg, Backa, Torslanda – and in the south – Högsbo, Frölunda, Askim – we reach you quickly via the E6 and E20 motorways.\n\nWe also cover all surrounding municipalities: ${göteborgKommunerText}. Cross-region deliveries, for example an office move from Lindholmen to Mölndal or a pallet delivery from Kungälv to Kungsbacka, are everyday business. We always plan the fastest route.`,
          },
          {
            heading: "Same-day delivery van in Gothenburg",
            content:
              "Most people looking for a delivery van in Gothenburg need the delivery today, not tomorrow. That is why we offer same-day delivery as standard across the Gothenburg region. You book, we confirm, and our driver is on site within a couple of hours. Need it faster? Choose express delivery – your job gets immediate priority and the van heads straight to the pickup address. In central Gothenburg, express delivery by small car can mean 30–60 minutes. With a van, expect one to two hours depending on distance and whether the route crosses Hisingen or goes south.",
          },
          {
            heading: "Delivery van for Gothenburg businesses – contracts and regular runs",
            content:
              "Running a business in Gothenburg with regular delivery needs? We offer business agreements with discounted delivery van rates, scheduled pickups and dedicated drivers who know your company. Tech companies at Lindholmen shipping equipment daily, restaurants in Haga with supplier deliveries, e-commerce businesses in Mölndal handling returns, and architecture studios at Lilla Bommen with material samples – everyone gets a tailored solution. With a business agreement, you skip booking each individual job. The van arrives on schedule, and you receive one invoice per month.",
          },
          {
            heading: "Why book a delivery van in Gothenburg with Ungdomskraft?",
            listItems: [
              {
                label: "Local Gothenburg knowledge –",
                text: "Our drivers operate in Gothenburg daily and know traffic patterns, bridges, parking options and shortcuts in every neighborhood. This means faster deliveries and lower cost.",
              },
              {
                label: "Carrying help always included –",
                text: "Our young drivers load, carry up stairs and deliver all the way inside. You don't need to lift a finger – whether it is a landshövdingehus in Majorna or an office at Lindholmen.",
              },
              {
                label: "Right van for the job –",
                text: "Small car for quick city errands, van for furniture, large van with tail lift for pallets. Always the right vehicle for Gothenburg traffic.",
              },
              {
                label: "Fixed price upfront –",
                text: "You see the price when booking. No hidden fees, no surcharges for central Gothenburg or surrounding municipalities.",
              },
              {
                label: "Supporting young Gothenburgers –",
                text: "Every delivery van you book gives a young person the chance to work and grow. Delivery van service with social impact since 2014.",
              },
            ],
          },
        ],
      },

      faq: [
        {
          question: "How much does a delivery van in Gothenburg cost?",
          answer:
            "The price depends on vehicle, distance and time. Small car from SEK 995, van from SEK 1,495, large van with tail lift from SEK 2,495. You see the price instantly when booking.",
        },
        {
          question: "How fast can the delivery van arrive in Gothenburg?",
          answer:
            "With standard delivery, the van arrives within 2–4 hours. With express delivery, your job is prioritized immediately – often within 30–60 minutes in central Gothenburg.",
        },
        {
          question: "Which Gothenburg neighborhoods do you cover?",
          answer:
            `We operate delivery vans across the entire Gothenburg region – the city center, Hisingen, southern suburbs and all surrounding municipalities: ${göteborgKommunerText}. No part of Gothenburg is outside our area.`,
        },
        {
          question: "Is carrying help included with the delivery van?",
          answer:
            "Yes, carrying help is always included. Our drivers load, carry up stairs and deliver all the way inside – whether it is a landshövdingehus in Majorna or an office building in Mölndal.",
        },
        {
          question: "Can the delivery van handle a EU pallet in Gothenburg?",
          answer:
            "Yes, our large van has a tail lift and handles EU pallets up to 800 kg. We load and unload with the lift – no forklift needed.",
        },
        {
          question: "Can I book a same-day delivery van in Gothenburg?",
          answer:
            "Yes, most delivery van jobs in Gothenburg are picked up and delivered the same day. We confirm the time immediately when you book.",
        },
        {
          question: "What vehicles do you use as delivery vans in Gothenburg?",
          answer:
            "Small car for quick deliveries downtown, van for larger volumes and furniture, large van with tail lift for pallets and heavy goods.",
        },
        {
          question: "Do you deliver to suburbs like Mölndal and Kungälv?",
          answer:
            "Yes, we operate delivery vans across the entire Gothenburg region including all surrounding municipalities. Deliveries from Kungälv to Mölndal are everyday business for us.",
        },
        {
          question: "Can the van pick up Blocket purchases in Gothenburg?",
          answer:
            "Yes, we pick up furniture, electronics and other items you have bought from private sellers in Gothenburg. Describe what needs picking up, enter addresses and we deliver it to your door the same day.",
        },
        {
          question: "Do you offer business contracts for delivery vans in Gothenburg?",
          answer:
            "Yes, we offer contract pricing for Gothenburg businesses with recurring delivery van needs. Discounted rates, scheduled pickups and consolidated invoicing.",
        },
        {
          question: "Do you handle fragile items in Gothenburg?",
          answer:
            "Yes, we transport neon signs, artwork, electronics and other fragile items in Gothenburg daily. We recommend proper packaging, but our drivers handle everything with care.",
        },
        {
          question: "Do you operate evenings and weekends in Gothenburg?",
          answer:
            "Yes, we offer delivery van service in Gothenburg on evenings and weekends. Availability may vary – you can see available times when booking.",
        },
        {
          question: "How do I book a delivery van in Gothenburg?",
          answer:
            "You book directly on our website. Describe what you want to transport, enter pickup and delivery addresses in Gothenburg, and see the price instantly.",
        },
        {
          question: "Are my goods insured in the delivery van?",
          answer:
            "Yes, all delivery van transports in Gothenburg are insured. For fragile or valuable items, we recommend letting us know in advance.",
        },
        {
          question: "What is the difference between a delivery van and a regular courier in Gothenburg?",
          answer:
            "A regular courier can be sent by electric moped or small car for light deliveries. A delivery van means a larger vehicle – van or van with tail lift – with a driver and carrying help for heavier goods.",
        },
        {
          question: "Can the delivery van make multiple stops in Gothenburg?",
          answer:
            "Yes, you can add multiple pickup and delivery addresses in the same booking. Perfect if you need to pick up from a supplier on Hisingen and deliver to offices downtown and in Mölndal.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bud,

      pricingRows: getPackagePricingRows("bud", "en"),

      dynamicHeadings: {
        threeSteps: "Delivery van in Gothenburg in 3 steps",
        step2Text:
          "Our driver picks up your goods in Gothenburg, loads the van and drives directly to the recipient. You receive confirmation when the delivery is complete.",
        step3Title: "Delivered",
        step3Desc:
          "Your goods have arrived. The driver has carried everything in and placed it at the right address in Gothenburg – the job is done.",
        ctaHeading: "Book a delivery van in Gothenburg today",
        ctaText:
          "Describe what you want to transport, enter pickup and delivery addresses in Gothenburg. You see the price instantly and can choose express delivery.",
      },
    },
  },

  {
    // Cykelbud Göteborg — config för /cykelbud-goteborg
    // Stadsspecifik sida för cykelbud i Göteborg.
    slug: "cykelbud-goteborg",
    serviceCategory: "budtjänst",
    city: "göteborg",

    meta: {
      title:
        "Cykelbud Göteborg – Miljövänlig Leverans Samma Dag | Ungdomskraft",
      description:
        "Boka cykelbud i Göteborg med leverans samma dag. Nollutsläpp, snabbast genom centrala Göteborg. Dokument, nycklar, blommor och småpaket. Expressbud inom 30 min.",
      h1: "Cykelbud Göteborg",
    },

    hero: {
      heading: "Grönt och snabbt cykelbud Göteborg",
      intro:
        "Behöver du skicka ett dokument, nycklar eller ett litet paket i Göteborg? Vårt cykelbud tar sig snabbast genom centrum – från Haga till Avenyn på minuter, helt utan utsläpp. Expressleverans inom 30 minuter mellan Göteborgs stadsdelar.",
      heroImage: "/hero-cykelbud.png",
      heroImageAlt:
        "Ungdomskraft cykelbud Göteborg – ung cykelkurir levererar paket snabbt i Göteborg",
    },

    featureCards: {
      headingText: "Ditt gröna bud i Göteborg",
      sinceText: "Cykelbud i Göteborg sedan 2014",
      storyText:
        "Sedan 2014 har Ungdomskraft skickat cykelbud i centrala Göteborg och närförorter. Våra unga bud känner varje cykelbana från Haga till Lindholmen, varje genväg genom Vasastaden och varje portkod i Linnéstaden – snabbare än bilen och bättre för miljön.",
      bookYourText: "Boka ditt cykelbud",
      bookDescText:
        "Beskriv vad du vill skicka, ange hämtadress och leveransadress i Göteborg. Du ser priset direkt – enkelt och snabbt.",
      vehicleTitleText: "Cykel och elmoped – nollutsläpp i Göteborg",
      vehicleDescText:
        "Våra cykelbud kör med cykel eller el-driven moped. Perfekt för centrala Göteborg där bilar fastnar vid Järntorget och Linnégatan. Inget bränsle, inga utsläpp, inga parkeringsproblem.",
    },

    itemsSection: {
      heading: "Vanliga cykelbud-leveranser i Göteborg",
      items: [
        {
          name: "Juridiska dokument",
          description:
            "Avtal, kontrakt och handlingar mellan kontor vid Lilla Bommen, Avenyn och Östra Hamngatan",
          image: "/copy-machine.webp",
        },
        {
          name: "Nycklar & passerkort",
          description:
            "Lägenhetsnycklar till mäklare, kontorsnycklar till nyanställda på Lindholmen och passerkort till besökare",
          image: "/toolbox.webp",
        },
        {
          name: "Blommor & presenter",
          description:
            "Buketter från blomsterhandlare i Haga, presenter och tårtor som ska fram samma dag i Göteborg",
          image: "/plant.webp",
        },
        {
          name: "Medicin & labbprover",
          description:
            "Recept från vårdcentraler, labbprover till Sahlgrenska och tidskritiska medicinska försändelser i Göteborg",
          image: "/mini-fridge.webp",
        },
        {
          name: "E-handelsreturer",
          description:
            "Returpaket, provkollektioner och expressleveranser till e-handlare och butiker i centrala Göteborg",
          image: "/box.webp",
        },
        {
          name: "Restaurangleveranser",
          description:
            "Luncher, catering-prover och specialkaffe från restauranger i Haga, Magasinsgatan och Tredje Långgatan",
          image: "/dishes.webp",
        },
      ],
    },

    seoContent: {
      mainHeading:
        "Cykelbud i Göteborg – snabbast genom centrum",
      mainText:
        "Göteborgs centrum är kompakt och platt – perfekta förutsättningar för cykelbud. Medan bilar fastnar i kö vid Järntorget, letar parkering längs Linnégatan och kör omvägar via Götatunneln tar cykeln den rakaste vägen rakt genom stan. Ungdomskrafts cykelbud tar sig från Haga till Avenyn på under fem minuter, från Linnéstaden till Inom Vallgraven på tre och från Vasastaden till Johanneberg utan att stanna i trafiken en enda gång.\n\nVåra cykelbud är unga Göteborgare som känner staden utan och innan. De vet att cykelbanan längs Rosenlundskanalen sparar tid jämfört med Första Långgatan, att Allén är den snabbaste vägen från Järntorget mot Nordstan och att porten till kontorshusen vid Lilla Bommen alltid är öppen mot kajen. Den lokalkännedomen gör att vi levererar snabbare än alla GPS-navigerade bilbud i Göteborg. Vi levererar dokument, nycklar, blommor, medicin, småpaket och andra mindre försändelser samma dag – ofta inom en timme. Varje leverans görs med cykel eller el-driven moped, vilket innebär noll koldioxidutsläpp mitt i Göteborgs stadskärna. För kontor vid Lilla Bommen, techbolag på Lindholmen, vårdcentraler i Johanneberg och restauranger i Haga är vårt cykelbud det snabbaste och grönaste alternativet.",
      preparationHeading: "Förbereda ditt cykelbud i Göteborg",
      preparationText:
        "Se till att din försändelse är färdigpackad och redo vid hämtning. Dokument bör ligga i kuvert eller mapp. Ömtåliga föremål som blommor eller tårta bör vara ordentligt förpackade. Ange portkod om hämtadressen har en – det är vanligt i Linnéstaden och Vasastaden. Meddela receptionen om du sitter i en kontorsbyggnad. Cykelbud i Göteborg passar försändelser upp till cirka 10 kg som ryms i en cykelväska. För tyngre eller större gods rekommenderar vi istället vårt bud med bil eller skåpbil.",
      additionalSections: [
        {
          heading: "Stadsdelar vi kör cykelbud i Göteborg",
          content:
            "Vårt huvudsakliga leveransområde för cykelbud i Göteborg är centrum och de närmaste stadsdelarna. I Inom Vallgraven, Haga, Linnéstaden, Vasastaden och Johanneberg har vi cykelbud redo dagligen med responstider på 15–30 minuter. Vi täcker även Majorna, Masthugget, Guldheden och Örgryte. Till Lindholmen och Eriksberg på Hisingen cyklar vi via Göta älvbron – snabbare än bilen vid rusningstid. I yttre stadsdelar som Högsbo, Frölunda och Kortedala rekommenderar vi istället vårt vanliga bud med bil eller elmoped.",
        },
        {
          heading: "Expresscykelbud i centrala Göteborg",
          content:
            "Centrala Göteborg är vårt snabbaste leveransområde för cykelbud. Med expressleverans prioriteras ditt uppdrag direkt och budet cyklar raka vägen från hämtadress till leveransadress utan omvägar. I centrum innebär det normalt 15–30 minuter dörr till dörr. Expressbudet är perfekt för tidskritiska dokument mellan kontor vid Lilla Bommen och Östra Hamngatan, nycklar som ska till en visning i Linnéstaden inom kvarten, labbprover från en vårdcentral i Johanneberg till Sahlgrenska, eller ett kontrakt som måste signeras i Haga innan klockan tre. Våra bud navigerar Göteborgs cykelbanor snabbare än någon bil i rusningstrafiken vid Järntorget.",
        },
        {
          heading: "Cykelbud för Göteborgsföretag",
          content:
            "Många företag i centrala Göteborg har dagliga leveransbehov som passar perfekt för cykelbud – dokument till kunder, nycklar till hyresgäster, prover till labb, paket till kontoret. Istället för att skicka en anställd med bil genom Göteborgstrafiken kan du anlita vårt cykelbud. Det är snabbare, billigare och stärker ert hållbarhetsarbete. Vi erbjuder företagsavtal med rabatterade priser, schemalagda hämtningar och dedikerade cykelbud som lär känna ert kontor. Techbolag på Lindholmen, advokatbyråer vid Lilla Bommen, vårdcentraler i Johanneberg, restauranger i Haga och kontorshotell vid Avenyn – alla kan dra nytta av ett grönt budavtal. Vi utfärdar gärna nollutsläppsintyg för er hållbarhetsredovisning.",
        },
        {
          heading: "Varför boka cykelbud i Göteborg med Ungdomskraft?",
          listItems: [
            {
              label: "Snabbast i centrum –",
              text: "Cykeln slår bilen i centrala Göteborg varje gång. Inga köer vid Järntorget, inga parkeringsproblem i Linnéstaden, inga omvägar via Götatunneln.",
            },
            {
              label: "Lokal Göteborgskunskap –",
              text: "Våra bud känner cykelbanor, genvägar och portkoder i varje stadsdel. Från Rosenlundskanalen till Lindholmen – lokalkännedom som ingen GPS kan ersätta.",
            },
            {
              label: "Nollutsläpp –",
              text: "Cykel eller elmoped laddad med grön el. Inget avgasrör, inget buller, inga partiklar mitt i Göteborgs centrum.",
            },
            {
              label: "Express inom 30 min –",
              text: "Expresscykelbud i centrala Göteborg levererar normalt inom 15–30 minuter. Perfekt för tidskritiska dokument och nycklar.",
            },
            {
              label: "Stödjer unga Göteborgare –",
              text: "Varje cykelbud du bokar ger en ung Göteborgare möjlighet att jobba och växa. Budtjänst med socialt engagemang sedan 2014.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Hur snabbt levererar cykelbud i Göteborg?",
        answer:
          "I centrala Göteborg levererar cykelbud vanligtvis inom 1–2 timmar. Med expressleverans kan vi ofta vara på plats inom 15–30 minuter mellan stadsdelar som Haga, Linnéstaden, Vasastaden och Inom Vallgraven.",
      },
      {
        question: "Vad kostar cykelbud i Göteborg?",
        answer:
          "Cykelbud i Göteborg börjar från 299 kr. Priset beror på avstånd och tid. Du ser exakt pris vid bokning – inga dolda kostnader.",
      },
      {
        question: "Vilka stadsdelar kör ni cykelbud i Göteborg?",
        answer:
          "Vi kör cykelbud i centrala Göteborg – Inom Vallgraven, Haga, Linnéstaden, Vasastaden, Johanneberg, Majorna, Masthugget och Örgryte. Vi täcker även Lindholmen och Eriksberg via Göta älvbron.",
      },
      {
        question: "Vad kan jag skicka med cykelbud i Göteborg?",
        answer:
          "Dokument, nycklar, småpaket, blommor, medicin, labbprover, mat och andra försändelser som ryms i en cykelväska – upp till cirka 10 kg.",
      },
      {
        question: "Kan ni köra expresscykelbud i centrala Göteborg?",
        answer:
          "Ja, expresscykelbud i centrum levereras normalt inom 15–30 minuter. Perfekt för tidskritiska dokument, nycklar och medicin mellan Göteborgs centrala stadsdelar.",
      },
      {
        question: "Är cykelbud i Göteborg verkligen utsläppsfritt?",
        answer:
          "Ja, våra cykelbud kör med cykel eller el-driven moped laddad med grön el. Inga fossila bränslen, inga avgaser, inga partiklar – mitt i Göteborgs centrum.",
      },
      {
        question: "Kan cykelbud leverera till Lindholmen?",
        answer:
          "Ja, vi cyklar till Lindholmen och Eriksberg via Göta älvbron. Det är ofta snabbare än att köra bil genom Tingstadstunneln vid rusningstid.",
      },
      {
        question: "Kan cykelbud leverera i regn i Göteborg?",
        answer:
          "Ja, våra Göteborgsbud kör i alla väder – och Göteborg har gott om regn. Försändelsen skyddas i vattentät väska och levereras torr och hel.",
      },
      {
        question: "Kan jag boka cykelbud i Göteborg samma dag?",
        answer:
          "Ja, de flesta cykelbud-uppdrag i Göteborg hämtas och levereras samma dag. Boka så tidigt som möjligt för bästa tillgänglighet.",
      },
      {
        question: "Erbjuder ni cykelbud-avtal för företag i Göteborg?",
        answer:
          "Ja, vi erbjuder företagsavtal för Göteborgsföretag med rabatterade priser, fasta hämtningstider och samlad fakturering. Vi utfärdar även nollutsläppsintyg för hållbarhetsredovisning.",
      },
      {
        question: "Kan cykelbud leverera blommor i Göteborg?",
        answer:
          "Ja, vi levererar buketter och blommor varsamt i speciella väskor. Populärt från blomsterhandlare i Haga och Linnéstaden till mottagare i hela centrum.",
      },
      {
        question: "Kan cykelbud leverera labbprover i Göteborg?",
        answer:
          "Ja, vi kör labbprover mellan vårdcentraler och sjukhus i Göteborg, inklusive Sahlgrenska. Tidskritiska medicinska försändelser prioriteras alltid.",
      },
      {
        question: "Kör ni cykelbud på kvällar och helger i Göteborg?",
        answer:
          "Ja, vi erbjuder cykelbud i Göteborg även på kvällar och helger. Tillgängligheten kan variera – du ser lediga tider vid bokning.",
      },
      {
        question: "Är mina saker försäkrade under cykelbudet i Göteborg?",
        answer:
          "Ja, alla cykelbud-leveranser i Göteborg är försäkrade under transporten. Kontakta oss i förväg vid särskilt värdefulla föremål.",
      },
      {
        question: "Vad är skillnaden mellan cykelbud och vanligt bud i Göteborg?",
        answer:
          "Cykelbud kör med cykel eller elmoped och passar mindre försändelser i centrum. Vanligt bud använder bil eller skåpbil och täcker hela Göteborgsregionen med tyngre gods.",
      },
      {
        question: "Varför är cykelbud snabbare än bilbud i Göteborg?",
        answer:
          "Cykeln tar den rakaste vägen, slipper köer vid Järntorget och Linnégatan, behöver inte leta parkering och kan använda cykelbanor som bilen inte har tillgång till. I centrala Göteborg är cykelbud ofta 20–40 minuter snabbare.",
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bud,

    pricingRows: getPackagePricingRows("bud", "sv"),

    dynamicHeadings: {
      threeSteps: "Cykelbud i Göteborg i 3 steg",
      step2Text:
        "Vårt cykelbud hämtar din försändelse i Göteborg och cyklar raka vägen till mottagaren. Du får en bekräftelse när leveransen är klar.",
      step3Title: "Levererat",
      step3Desc:
        "Din försändelse har anlänt – snabbt, grönt och utan utsläpp genom centrala Göteborg.",
      ctaHeading: "Boka cykelbud i Göteborg idag",
      ctaText:
        "Beskriv vad du vill skicka, ange hämtadress och leveransadress i Göteborg. Du ser priset direkt – snabbt, enkelt och miljövänligt.",
    },

    en: {
      slug: "bicycle-courier-gothenburg",
      meta: {
        title:
          "Bicycle Courier Gothenburg – Eco-Friendly Same Day Delivery | Ungdomskraft",
        description:
          "Book a bicycle courier in Gothenburg with same-day delivery. Zero emissions, fastest through central Gothenburg. Documents, keys, flowers and small parcels. Express within 30 min.",
        h1: "Bicycle Courier Gothenburg",
      },

      hero: {
        heading: "Green and fast bicycle courier Gothenburg",
        intro:
          "Need to send a document, keys or a small parcel in Gothenburg? Our bicycle courier is the fastest way through the city center – from Haga to Avenyn in minutes, with zero emissions. Express delivery within 30 minutes between Gothenburg's neighborhoods.",
        heroImageAlt:
          "Ungdomskraft bicycle courier Gothenburg – young bike courier delivering packages fast in Gothenburg",
      },

      featureCards: {
        headingText: "Your green courier in Gothenburg",
        sinceText: "Bicycle courier in Gothenburg since 2014",
        storyText:
          "Since 2014, Ungdomskraft has been sending bicycle couriers across central Gothenburg and nearby neighborhoods. Our young couriers know every cycle path from Haga to Lindholmen, every shortcut through Vasastaden and every entry code in Linnéstaden – faster than a car and better for the environment.",
        bookYourText: "Book your bicycle courier",
        bookDescText:
          "Describe what you want to send, enter pickup and delivery addresses in Gothenburg. You see the price instantly – simple and fast.",
        vehicleTitleText: "Bicycle and electric moped – zero emissions in Gothenburg",
        vehicleDescText:
          "Our bicycle couriers ride bikes or electric mopeds. Perfect for central Gothenburg where cars get stuck at Järntorget and Linnégatan. No fuel, no emissions, no parking problems.",
      },

      itemsSection: {
        heading: "Common bicycle courier deliveries in Gothenburg",
        items: [
          {
            name: "Legal documents",
            description:
              "Agreements, contracts and legal papers between offices at Lilla Bommen, Avenyn and Östra Hamngatan",
            image: "/copy-machine.webp",
          },
          {
            name: "Keys & access cards",
            description:
              "Apartment keys to real estate agents, office keys to new employees at Lindholmen and access cards to visitors",
            image: "/toolbox.webp",
          },
          {
            name: "Flowers & gifts",
            description:
              "Bouquets from florists in Haga, gifts and cakes that need to arrive same day in Gothenburg",
            image: "/plant.webp",
          },
          {
            name: "Medicine & lab samples",
            description:
              "Prescriptions from health clinics, lab samples to Sahlgrenska and time-critical medical shipments in Gothenburg",
            image: "/mini-fridge.webp",
          },
          {
            name: "E-commerce returns",
            description:
              "Return packages, sample collections and express deliveries to online retailers and stores in central Gothenburg",
            image: "/box.webp",
          },
          {
            name: "Restaurant deliveries",
            description:
              "Lunches, catering samples and specialty coffee from restaurants in Haga, Magasinsgatan and Tredje Långgatan",
            image: "/dishes.webp",
          },
        ],
      },

      seoContent: {
        mainHeading:
          "Bicycle courier in Gothenburg – fastest through the city center",
        mainText:
          "Gothenburg's city center is compact and flat – perfect conditions for bicycle couriers. While cars get stuck in traffic at Järntorget, search for parking along Linnégatan and take detours via the Göta Tunnel, the bicycle takes the most direct route straight through town. Ungdomskraft's bicycle couriers go from Haga to Avenyn in under five minutes, from Linnéstaden to Inom Vallgraven in three, and from Vasastaden to Johanneberg without stopping in traffic once.\n\nOur bicycle couriers are young Gothenburgers who know the city inside and out. They know that the cycle path along Rosenlundskanalen saves time compared to Första Långgatan, that Allén is the fastest route from Järntorget towards Nordstan, and that the doors to the office buildings at Lilla Bommen are always open from the quay side. That local knowledge means we deliver faster than any GPS-navigated car courier in Gothenburg. We deliver documents, keys, flowers, medicine, small parcels and other smaller shipments same day – often within one hour. Every delivery is made by bicycle or electric moped, meaning zero carbon emissions right in Gothenburg's city center. For offices at Lilla Bommen, tech companies at Lindholmen, health clinics in Johanneberg and restaurants in Haga, our bicycle courier is the fastest and greenest option.",
        preparationHeading: "Preparing your bicycle courier in Gothenburg",
        preparationText:
          "Make sure your shipment is packed and ready at pickup. Documents should be in an envelope or folder. Fragile items like flowers or cakes should be properly packaged. Provide an entry code if the pickup address has one – this is common in Linnéstaden and Vasastaden. Let the reception know if you are in an office building. Bicycle courier in Gothenburg is suitable for shipments up to approximately 10 kg that fit in a courier bag. For heavier or larger goods, we recommend our regular courier with car or van instead.",
        additionalSections: [
          {
            heading: "Neighborhoods we cover by bicycle in Gothenburg",
            content:
              "Our main delivery area for bicycle couriers in Gothenburg is the city center and nearest neighborhoods. In Inom Vallgraven, Haga, Linnéstaden, Vasastaden and Johanneberg we have bicycle couriers ready daily with response times of 15–30 minutes. We also cover Majorna, Masthugget, Guldheden and Örgryte. We cycle to Lindholmen and Eriksberg on Hisingen via the Göta Älv Bridge – often faster than driving through the Tingstad Tunnel at rush hour. For outer neighborhoods like Högsbo, Frölunda and Kortedala, we recommend our regular courier with car or electric moped.",
          },
          {
            heading: "Express bicycle courier in central Gothenburg",
            content:
              "Central Gothenburg is our fastest delivery zone for bicycle couriers. With express delivery, your job is prioritized immediately and the courier cycles straight from pickup to delivery without detours. In the city center, this normally means 15–30 minutes door to door. The express courier is perfect for time-critical documents between offices at Lilla Bommen and Östra Hamngatan, keys that need to reach a showing in Linnéstaden within fifteen minutes, lab samples from a health clinic in Johanneberg to Sahlgrenska, or a contract that needs signing in Haga before three o'clock. Our couriers navigate Gothenburg's cycle paths faster than any car in rush hour traffic at Järntorget.",
          },
          {
            heading: "Bicycle courier for Gothenburg businesses",
            content:
              "Many businesses in central Gothenburg have daily delivery needs that are a perfect fit for bicycle couriers – documents to clients, keys to tenants, samples to labs, packages to the office. Instead of sending an employee by car through Gothenburg traffic, you can use our bicycle courier. It's faster, cheaper and strengthens your sustainability efforts. We offer business agreements with discounted rates, scheduled pickups and dedicated bicycle couriers who get to know your office. Tech companies at Lindholmen, law firms at Lilla Bommen, health clinics in Johanneberg, restaurants in Haga and co-working spaces on Avenyn – all can benefit from a green courier agreement. We're happy to issue zero-emission certificates for your sustainability reporting.",
          },
          {
            heading: "Why book a bicycle courier in Gothenburg with Ungdomskraft?",
            listItems: [
              {
                label: "Fastest in the city center –",
                text: "The bicycle beats the car in central Gothenburg every time. No queues at Järntorget, no parking problems in Linnéstaden, no detours via the Göta Tunnel.",
              },
              {
                label: "Local Gothenburg knowledge –",
                text: "Our couriers know cycle paths, shortcuts and entry codes in every neighborhood. From Rosenlundskanalen to Lindholmen – local knowledge that no GPS can replace.",
              },
              {
                label: "Zero emissions –",
                text: "Bicycle or electric moped charged with green electricity. No exhaust, no noise, no particulates in central Gothenburg.",
              },
              {
                label: "Express within 30 min –",
                text: "Express bicycle courier in central Gothenburg delivers within 15–30 minutes. Perfect for time-critical documents and keys.",
              },
              {
                label: "Supporting young Gothenburgers –",
                text: "Every bicycle courier you book gives a young Gothenburger the opportunity to work and grow. Courier service with social impact since 2014.",
              },
            ],
          },
        ],
      },

      faq: [
        {
          question: "How fast does a bicycle courier deliver in Gothenburg?",
          answer:
            "In central Gothenburg, bicycle couriers typically deliver within 1–2 hours. With express delivery, we can often arrive within 15–30 minutes between neighborhoods like Haga, Linnéstaden, Vasastaden and Inom Vallgraven.",
        },
        {
          question: "How much does a bicycle courier in Gothenburg cost?",
          answer:
            "Bicycle courier in Gothenburg starts from SEK 299. The price depends on distance and time. You see the exact price when booking – no hidden costs.",
        },
        {
          question: "Which Gothenburg neighborhoods do you cover by bicycle?",
          answer:
            "We cover central Gothenburg – Inom Vallgraven, Haga, Linnéstaden, Vasastaden, Johanneberg, Majorna, Masthugget and Örgryte. We also cover Lindholmen and Eriksberg via the Göta Älv Bridge.",
        },
        {
          question: "What can I send by bicycle courier in Gothenburg?",
          answer:
            "Documents, keys, small parcels, flowers, medicine, lab samples, food and other shipments that fit in a courier bag – up to approximately 10 kg.",
        },
        {
          question: "Can you do express bicycle courier in central Gothenburg?",
          answer:
            "Yes, express bicycle courier in the city center delivers within 15–30 minutes. Perfect for time-critical documents, keys and medicine between Gothenburg's central neighborhoods.",
        },
        {
          question: "Is bicycle courier in Gothenburg really zero-emission?",
          answer:
            "Yes, our bicycle couriers ride bikes or electric mopeds charged with green electricity. No fossil fuels, no exhaust, no particulates – right in central Gothenburg.",
        },
        {
          question: "Can bicycle couriers deliver to Lindholmen?",
          answer:
            "Yes, we cycle to Lindholmen and Eriksberg via the Göta Älv Bridge. It is often faster than driving through the Tingstad Tunnel at rush hour.",
        },
        {
          question: "Can bicycle couriers deliver in rain in Gothenburg?",
          answer:
            "Yes, our Gothenburg couriers ride in all weather – and Gothenburg has plenty of rain. The shipment is protected in a waterproof bag and delivered dry and intact.",
        },
        {
          question: "Can I book a bicycle courier in Gothenburg the same day?",
          answer:
            "Yes, most bicycle courier jobs in Gothenburg are picked up and delivered the same day. Book as early as possible for best availability.",
        },
        {
          question: "Do you offer bicycle courier contracts for Gothenburg businesses?",
          answer:
            "Yes, we offer business agreements for Gothenburg companies with discounted rates, scheduled pickups and consolidated invoicing. We also issue zero-emission certificates for sustainability reporting.",
        },
        {
          question: "Can bicycle couriers deliver flowers in Gothenburg?",
          answer:
            "Yes, we deliver bouquets and flowers carefully in special bags. Popular from florists in Haga and Linnéstaden to recipients across the city center.",
        },
        {
          question: "Can bicycle couriers deliver lab samples in Gothenburg?",
          answer:
            "Yes, we deliver lab samples between health clinics and hospitals in Gothenburg, including Sahlgrenska. Time-critical medical shipments are always prioritized.",
        },
        {
          question: "Do you offer bicycle courier on evenings and weekends in Gothenburg?",
          answer:
            "Yes, we offer bicycle courier in Gothenburg on evenings and weekends. Availability may vary – you can see available times when booking.",
        },
        {
          question: "Are my items insured during bicycle courier in Gothenburg?",
          answer:
            "Yes, all bicycle courier deliveries in Gothenburg are insured during transport. Contact us in advance for particularly valuable items.",
        },
        {
          question: "What is the difference between bicycle courier and regular courier in Gothenburg?",
          answer:
            "Bicycle courier uses bikes or electric mopeds for smaller shipments in the city center. Regular courier uses cars or vans and covers the entire Gothenburg region with heavier goods.",
        },
        {
          question: "Why is bicycle courier faster than car courier in Gothenburg?",
          answer:
            "The bicycle takes the most direct route, avoids queues at Järntorget and Linnégatan, doesn't need to search for parking and can use cycle paths that cars can't access. In central Gothenburg, bicycle courier is often 20–40 minutes faster.",
        },
      ],

      priceHint: PACKAGE_PRICE_HINT_EN.bud,

      pricingRows: getPackagePricingRows("bud", "en"),

      dynamicHeadings: {
        threeSteps: "Bicycle courier in Gothenburg in 3 steps",
        step2Text:
          "Our bicycle courier picks up your shipment in Gothenburg and cycles straight to the recipient. You receive confirmation when the delivery is complete.",
        step3Title: "Delivered",
        step3Desc:
          "Your shipment has arrived – fast, green and with zero emissions through central Gothenburg.",
        ctaHeading: "Book a bicycle courier in Gothenburg today",
        ctaText:
          "Describe what you want to send, enter pickup and delivery addresses in Gothenburg. You see the price instantly – fast, simple and eco-friendly.",
      },
    },
  },
];
